/**
 * Filename: App.tsx
 * Description: Administration console shell and content, user, billing, and vocabulary asset workspaces.
 * Created by: Teddy Gil "Shin" Ravis Orbeta
 * Created: 2026-07-26
 * Updated: 2026-09-27
 */

import ExcelJS from 'exceljs';
import { jsPDF } from 'jspdf';
import React, { FormEvent, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';

import './App.css';
import {
  AuditWorkspace,
  BillingOperationsPanel,
  ContentOperationsPanel,
  PrivacyWorkspace,
  SafetyWorkspace,
  SupportWorkspace,
  SystemOperationsWorkspace,
} from './AdminOperationsWorkspaces';
import { AnimationsAndSoundsWorkspace } from './AnimationsAndSoundsWorkspace';
import {
  CurriculumContentPreview,
  curriculumModeFromHash,
  type CurriculumMode,
} from './CurriculumContentPreview';
import { LegalContentWorkspace } from './LegalContentWorkspace';
import { SecurityThreatReport } from './SecurityThreatReport';
import { ShinQLoader } from './ShinQLoader';
import {
  DASHBOARD_NAV_ITEMS,
  dashboardSectionFromHash,
  dashboardSectionUrl,
  type DashboardSection,
} from './dashboard-navigation';
import {
  translateJapaneseFloorAndRoom,
  type PostalCodeAddress,
} from './utils/postal-code';
import {
  loadPostalDirectory,
  normalizePostalSearchValue,
  searchPostalDirectory,
  type PostalDirectoryEntry,
} from './utils/postal-directory';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.dev.shinq21.com';
const IS_NON_PRODUCTION_ADMIN = API_URL === 'https://api.dev.shinq21.com' || API_URL === 'https://api.staging.shinq21.com';
const THEME_TRANSITION_DURATION_MS = 420;

function encodeAdminOffsetCursor(offset: number): string {
  return window.btoa(JSON.stringify({ kind: 'offset', offset }))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/u, '');
}
function vocabularyPrefixRank(word: string, query: string): number {
  const normalizedWord = word.trim().toLowerCase();
  const normalizedQuery = query.trim().toLowerCase();
  if (normalizedWord === normalizedQuery) return 0;
  if (normalizedWord.startsWith(normalizedQuery)) return 1;
  return Number.POSITIVE_INFINITY;
}

const CONTENT_TOOL_GROUPS: Array<{
  title: string;
  tools: Array<[CurriculumMode, string]>;
}> = [
  {
    title: "Today's Learning",
    tools: [['today', "Today's Lesson"]],
  },
  {
    title: 'Study Tools',
    tools: [
      ['vocabulary', 'Vocabulary'],
      ['flashcards', 'Flashcards'],
      ['listening', 'Listening'],
      ['spelling', 'Spelling bee'],
      ['ai', 'AI questions'],
    ],
  },
  {
    title: 'Quick Practice',
    tools: [
      ['pronunciation', 'Pronunciation'],
      ['reading', 'Reading'],
      ['dictation', 'Dictation'],
      ['translation', 'Translation'],
      ['mistakes', 'Mistakes'],
      ['mixed', 'Mixed practice'],
    ],
  },
  {
    title: 'Play and Download',
    tools: [
      ['tournament', 'Tournaments'],
      ['offline', 'Offline mode'],
    ],
  },
  {
    title: 'Exam Readiness',
    tools: [
      ['ielts-academic', 'IELTS Academic'],
      ['ielts-general', 'IELTS General Training'],
      ['toeic', 'TOEIC'],
      ['toefl', 'TOEFL iBT'],
    ],
  },
  {
    title: 'English Level Check',
    tools: [
      ['assessment-reading', 'Reading'],
      ['assessment-listening', 'Listening'],
      ['assessment-writing', 'Writing'],
      ['assessment-speaking', 'Speaking'],
      ['assessment-grammar', 'Grammar'],
      ['assessment-vocabulary', 'Vocabulary'],
    ],
  },
];

type Level = 'pre_basic' | 'beginner' | 'intermediate' | 'advanced' | 'mastery';
type AssetStatus = 'all' | 'complete' | 'missing_image' | 'missing_audio' | 'review' | 'approved';
type ImageModelChoice = 'openai-gpt-image-2-medium' | 'aws-stable-image-core-v1.1';

const IMAGE_MODELS: Array<{
  id: ImageModelChoice;
  label: string;
  provider: string;
  kind: string;
  quality: string;
  region: string;
  price: string;
  description: string;
}> = [
  {
    id: 'openai-gpt-image-2-medium',
    label: 'OpenAI GPT Image 2 — Medium',
    provider: 'OpenAI',
    kind: 'State-of-the-art text-to-image model',
    quality: 'Medium · 1536 × 1024 landscape',
    region: 'Processed by OpenAI; accepted image stored in Tokyo S3',
    price: 'About $0.047 per image output',
    description: 'Best default for accurate vocabulary scenes and prompt following.',
  },
  {
    id: 'aws-stable-image-core-v1.1',
    label: 'AWS Stable Image Core v1.1',
    provider: 'Stability AI through Amazon Bedrock',
    kind: 'Fast, economical text-to-image model',
    quality: 'Standard · 1:1 square',
    region: 'Processed in Oregon (us-west-2); accepted image stored in Tokyo S3',
    price: 'About $0.04 per generation',
    description: 'Lower-cost alternative for simple objects and uncomplicated scenes.',
  },
];

interface VocabularySense {
  senseId: string;
  definition: string;
  japaneseTranslation: string;
  japaneseDefinition?: string;
  dictionaryReferences?: Array<{
    provider: 'oxford' | 'merriam_webster' | 'japanese_licensed_dictionary';
    entryId?: string;
    sourceUrl?: string;
    verifiedAt: string;
  }>;
  usageContext: string;
  exampleSentence: string;
}

interface VocabularyImage {
  imageId: string;
  master1024Url: string;
  mobile512Url: string;
  thumbnail256Url: string;
  description: string;
  uploadedAt: string;
  verifiedAt?: string;
}

interface VocabularyItem {
  vocabId: string;
  englishWord: string;
  pronunciation: string;
  partOfSpeech: string;
  proficiencyLevel: Level;
  difficulty: number;
  contentStatus: string;
  confidenceScore: number;
  senses: VocabularySense[];
  exampleSentences: string[];
  synonyms: string[];
  antonyms: string[];
  images: VocabularyImage[];
  audioAmericanUrl?: string;
  audioBritishUrl?: string;
  adminReviewStatus?: 'pending' | 'approved' | 'changes_requested';
  adminReviewNotes?: string;
  updatedAt: string;
  imageGenerationFailureReason?: string;
}

interface Credentials {
  environmentToken: string;
  accessToken: string;
}

type AdminAccessLevel = 'read' | 'write' | 'full';
type AdminSection = DashboardSection;
type AdminPermission = 'users.sensitive.reveal' | 'security.view' | 'security.investigate' | 'security.resolve' | 'security.block' | 'security.unblock' | 'safety.decrypt' | 'audit.view';

const ADMIN_PERMISSION_OPTIONS: ReadonlyArray<{ value: AdminPermission; label: string }> = [
  { value: 'users.sensitive.reveal', label: 'Reveal sensitive user data' },
  { value: 'security.view', label: 'View security incidents' },
  { value: 'security.investigate', label: 'Investigate security incidents' },
  { value: 'security.resolve', label: 'Resolve security incidents' },
  { value: 'security.block', label: 'Block abusive IP addresses' },
  { value: 'security.unblock', label: 'Revoke IP address blocks' },
  { value: 'safety.decrypt', label: 'Decrypt critical safety content' },
  { value: 'audit.view', label: 'View administrator audit events' },
];

interface AdminRole {
  adminId: string;
  username: string;
  email: string;
  status?: string;
  accessLevel: AdminAccessLevel;
  sections: AdminSection[];
  permissions: AdminPermission[];
  isPrimary: boolean;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

interface AssetSummary {
  results: number;
  complete: number;
  missingImage: number;
  missingAudio: number;
  approved: number;
}

const EMPTY_ASSET_SUMMARY: AssetSummary = {
  results: 0,
  complete: 0,
  missingImage: 0,
  missingAudio: 0,
  approved: 0,
};

const storedCredentials = (): Credentials => ({
  environmentToken: sessionStorage.getItem('shinq_admin_environment_token') || '',
  accessToken: sessionStorage.getItem('shinq_admin_access_token') || '',
});

function App() {
  const [colorMode, setColorMode] = useState<'light' | 'dark'>(() => localStorage.getItem('shinq-admin-theme') === 'dark' ? 'dark' : 'light');
  const themeTransitionTimer = useRef<number>();
  const [credentials, setCredentials] = useState(storedCredentials);
  const [adminSession, setAdminSession] = useState<AdminRole>();
  const [sessionLoading, setSessionLoading] = useState(Boolean(storedCredentials().accessToken));
  const [environmentUsername, setEnvironmentUsername] = useState('');
  const [environmentPassword, setEnvironmentPassword] = useState('');
  const [environmentPasswordVisible, setEnvironmentPasswordVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [adminPasswordVisible, setAdminPasswordVisible] = useState(false);
  const [authError, setAuthError] = useState('');
  const [busy, setBusy] = useState(false);
  const [section, setSection] = useState<DashboardSection>(
    () => dashboardSectionFromHash(window.location.hash),
  );
  useEffect(() => {
    document.documentElement.dataset.theme = colorMode;
    localStorage.setItem('shinq-admin-theme', colorMode);
  }, [colorMode]);
  useEffect(() => () => {
    if (themeTransitionTimer.current) window.clearTimeout(themeTransitionTimer.current);
  }, []);

  const toggleColorMode = () => {
    if (themeTransitionTimer.current) window.clearTimeout(themeTransitionTimer.current);
    document.documentElement.classList.add('admin-theme-changing');
    setColorMode((mode) => mode === 'light' ? 'dark' : 'light');
    themeTransitionTimer.current = window.setTimeout(() => {
      document.documentElement.classList.remove('admin-theme-changing');
    }, THEME_TRANSITION_DURATION_MS + 40);
  };

  const [items, setItems] = useState<VocabularyItem[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<AssetSummary>(EMPTY_ASSET_SUMMARY);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [level, setLevel] = useState<Level | ''>('');
  const [assetStatus, setAssetStatus] = useState<AssetStatus>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState<VocabularyItem | null>(null);
  const [replacingImage, setReplacingImage] = useState<VocabularyItem | null>(null);
  const pageSize = 20;

  const api = useCallback(async (path: string, init: RequestInit = {}) => {
    const response = await fetch(`${API_URL}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        'X-Environment-Access': credentials.environmentToken,
        Authorization: `Bearer ${credentials.accessToken}`,
        ...(init.headers || {}),
      },
    });
    const payload = await response.json().catch(() => ({}));
    if (response.status === 401) {
      sessionStorage.removeItem('shinq_admin_environment_token');
      sessionStorage.removeItem('shinq_admin_access_token');
      setCredentials({ environmentToken: '', accessToken: '' });
      throw new Error('Your development session expired. Please sign in again.');
    }
    if (response.status === 403) {
      if (payload.code === 'ADMIN_AUTH_REQUIRED' || payload.error === 'Administrator access required.') {
        sessionStorage.removeItem('shinq_admin_access_token');
        setCredentials((current) => ({ environmentToken: current.environmentToken, accessToken: '' }));
        throw new Error('Your administrator session expired. Please sign in again.');
      }
      throw new Error(payload.error || 'Your administrator role does not allow this action.');
    }
    if (!response.ok) throw new Error(payload.error || `Request failed (${response.status})`);
    return payload;
  }, [credentials]);

  useEffect(() => {
    if (!credentials.environmentToken || !credentials.accessToken) {
      setAdminSession(undefined);
      setSessionLoading(false);
      return;
    }
    setSessionLoading(true);
    api('/admin/session')
      .then((result) => setAdminSession(result.admin as AdminRole))
      .catch((requestError) => setAuthError(requestError instanceof Error ? requestError.message : 'Unable to load administrator permissions.'))
      .finally(() => setSessionLoading(false));
  }, [api, credentials.accessToken, credentials.environmentToken]);

  const canSeeSection = useCallback((target: AdminSection) => Boolean(
    adminSession && (
      adminSession.isPrimary
      || (
        (adminSession.accessLevel === 'full' || adminSession.sections.includes(target))
        && (target !== 'security' || adminSession.permissions?.includes('security.view'))
        && (target !== 'audit' || adminSession.permissions?.includes('audit.view'))
      )
    ),
  ), [adminSession]);
  const canWriteSection = useCallback((target: AdminSection) => Boolean(
    adminSession && (adminSession.isPrimary || adminSession.accessLevel === 'full' || (adminSession.accessLevel === 'write' && adminSession.sections.includes(target))),
  ), [adminSession]);

  const loadVocabulary = useCallback(async () => {
    if (!credentials.environmentToken || !credentials.accessToken) return;
    setLoading(true);
    setError('');
    const normalizedSearch = search.trim().toLowerCase();
    const params = new URLSearchParams({
      limit: String(normalizedSearch ? 100 : pageSize),
      assetStatus,
    });
    if (!normalizedSearch && currentPage > 1) params.set('cursor', encodeAdminOffsetCursor((currentPage - 1) * pageSize));
    if (normalizedSearch) params.set('search', normalizedSearch);
    if (level) params.set('level', level);
    try {
      let result = await api(`/admin/vocabulary?${params}`);
      if (normalizedSearch) {
        const candidates: VocabularyItem[] = [...(result.vocabulary || [])];
        while (result.nextCursor) {
          params.set('cursor', result.nextCursor);
          result = await api(`/admin/vocabulary?${params}`);
          candidates.push(...(result.vocabulary || []));
        }
        const prefixMatches = candidates
          .filter((item) => Number.isFinite(vocabularyPrefixRank(item.englishWord, normalizedSearch)))
          .sort((left, right) => vocabularyPrefixRank(left.englishWord, normalizedSearch) - vocabularyPrefixRank(right.englishWord, normalizedSearch)
            || left.englishWord.localeCompare(right.englishWord)
            || left.vocabId.localeCompare(right.vocabId));
        const offset = (currentPage - 1) * pageSize;
        setItems(prefixMatches.slice(offset, offset + pageSize));
        setTotal(prefixMatches.length);
        setSummary({
          results: prefixMatches.length,
          complete: prefixMatches.filter((item) => Boolean(item.images?.length && item.audioAmericanUrl && item.audioBritishUrl)).length,
          missingImage: prefixMatches.filter((item) => !item.images?.length).length,
          missingAudio: prefixMatches.filter((item) => !item.audioAmericanUrl || !item.audioBritishUrl).length,
          approved: prefixMatches.filter((item) => item.adminReviewStatus === 'approved').length,
        });
      } else {
        setItems(result.vocabulary || []);
        setTotal(result.total || 0);
        setSummary(result.summary || EMPTY_ASSET_SUMMARY);
      }
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to load vocabulary.');
    } finally {
      setLoading(false);
    }
  }, [api, assetStatus, currentPage, level, search]);

  useEffect(() => {
    if (section !== 'assets' || !canSeeSection('assets')) return undefined;
    const timer = window.setTimeout(loadVocabulary, 250);
    return () => window.clearTimeout(timer);
  }, [canSeeSection, loadVocabulary, section]);

  const authenticate = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setAuthError('');
    try {
      let environmentToken = credentials.environmentToken;
      if (!environmentToken) {
        const environmentResponse = await fetch(`${API_URL}/environment/access`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: environmentUsername, password: environmentPassword }),
        });
        const environmentPayload = await environmentResponse.json();
        if (!environmentResponse.ok) throw new Error(environmentPayload.error || 'Environment access failed.');
        environmentToken = environmentPayload.token;
      }
      const signInResponse = await fetch(`${API_URL}/auth/sign-in`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Environment-Access': environmentToken },
        body: JSON.stringify({ email, password }),
      });
      const signInPayload = await signInResponse.json();
      if (!signInResponse.ok) throw new Error(signInPayload.error || 'Sign in failed.');
      const accessToken = signInPayload.tokens?.accessToken;
      if (!accessToken) throw new Error('The sign-in response did not include an access token.');
      sessionStorage.setItem('shinq_admin_environment_token', environmentToken);
      sessionStorage.setItem('shinq_admin_access_token', accessToken);
      setCredentials({ environmentToken, accessToken });
    } catch (requestError) {
      setAuthError(requestError instanceof Error ? requestError.message : 'Unable to sign in.');
    } finally {
      setBusy(false);
    }
  };

  const signOut = () => {
    sessionStorage.removeItem('shinq_admin_environment_token');
    sessionStorage.removeItem('shinq_admin_access_token');
    setCredentials({ environmentToken: '', accessToken: '' });
    setAdminSession(undefined);
    setItems([]);
  };

  const mutate = async (item: VocabularyItem, action: string, body: Record<string, unknown>) => {
    const scrollPosition = window.scrollY;
    setNotice('');
    setError('');
    try {
      const result = await api(`/admin/vocabulary/${encodeURIComponent(item.vocabId)}${action}`, {
        method: action ? 'POST' : 'PATCH',
        body: JSON.stringify(body),
      });
      setNotice(result.queued ? `${item.englishWord}: regeneration queued.` : `${item.englishWord}: saved.`);
      await loadVocabulary();
      window.requestAnimationFrame(() => window.scrollTo({ top: scrollPosition, behavior: 'auto' }));
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Action failed.');
      window.requestAnimationFrame(() => window.scrollTo({ top: scrollPosition, behavior: 'auto' }));
    }
  };

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(''), 5000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    const syncSectionFromUrl = () => setSection(dashboardSectionFromHash(window.location.hash));
    window.addEventListener('hashchange', syncSectionFromUrl);
    window.addEventListener('popstate', syncSectionFromUrl);
    return () => {
      window.removeEventListener('hashchange', syncSectionFromUrl);
      window.removeEventListener('popstate', syncSectionFromUrl);
    };
  }, []);

  const selectSection = (nextSection: DashboardSection) => {
    if (!canSeeSection(nextSection)) return;
    if (nextSection === section) return;
    window.history.pushState(null, '', dashboardSectionUrl(nextSection, window.location));
    setSection(nextSection);
  };

  const uploadImage = async (item: VocabularyItem, file?: File) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('Upload a PNG, JPEG, or WebP image.');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      setError('The image must be no larger than 4 MB.');
      return;
    }
    const reason = window.prompt(`Why do you want to use this image for “${item.englishWord}”?`, '');
    if (reason === null) return;
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('Could not read the selected image.'));
        reader.readAsDataURL(file);
      });
      const base64 = dataUrl.split(',')[1];
      if (!base64) throw new Error('Could not read the selected image.');
      await mutate(item, '/upload-image', { contentType: file.type, base64, reason: reason.trim() });
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Image upload failed.');
    }
  };

  const reuseImage = async (item: VocabularyItem) => {
    const sourceWord = window.prompt(`Which vocabulary word's image should “${item.englishWord}” reuse?`, '');
    if (!sourceWord?.trim()) return;
    await mutate(item, '/reuse-image', { sourceWord: sourceWord.trim() });
  };

  const requestChanges = async (item: VocabularyItem) => {
    const notes = window.prompt('Describe exactly what must be changed:', item.adminReviewNotes || '');
    if (!notes?.trim()) return;
    await mutate(item, '', { action: 'request_changes', notes });
  };

  const removeAsset = async (item: VocabularyItem, target: 'image' | 'american_audio' | 'british_audio' | 'all_audio' | 'entry') => {
    const confirmation = window.prompt(`Type “${item.englishWord}” to confirm removal. Stored files are retained for recovery.`);
    if (confirmation !== item.englishWord) return;
    try {
      await api(`/admin/vocabulary/${encodeURIComponent(item.vocabId)}`, {
        method: 'DELETE',
        body: JSON.stringify({ target, confirmWord: confirmation }),
      });
      setNotice(`${item.englishWord}: ${target.replace('_', ' ')} removed from the learner catalog.`);
      await loadVocabulary();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Removal failed.');
    }
  };

  const pageMetrics = useMemo(() => ({
    results: items.length,
    complete: items.filter((item) => item.images?.length && item.audioAmericanUrl && item.audioBritishUrl).length,
    missingImage: items.filter((item) => !item.images?.length).length,
    missingAudio: items.filter((item) => !item.audioAmericanUrl || !item.audioBritishUrl).length,
    approved: items.filter((item) => item.adminReviewStatus === 'approved').length,
  }), [items]);

  const selectAssetStatus = (status: AssetStatus) => {
    setAssetStatus(status);
    setCurrentPage(1);
  };

  if (!credentials.environmentToken || !credentials.accessToken) {
    return (
      <main className="login-page">
        <ThemeModeButton colorMode={colorMode} onClick={toggleColorMode} className="login-theme-toggle" />
        <form className="login-card" onSubmit={authenticate}>
          <AdminBrandMark />
          <p className="eyebrow">Restricted administration</p>
          <h1>Content review</h1>
          <p className="muted">Development access and an administrator account are both required.</p>
          {!credentials.environmentToken && (
            <div className="form-section">
              <h2>Development environment</h2>
              <label>Environment username<input value={environmentUsername} onChange={(event) => setEnvironmentUsername(event.target.value)} autoComplete="username" /></label>
              <label>
                Environment password
                <span className="password-shell">
                  <input
                    type={environmentPasswordVisible ? 'text' : 'password'}
                    value={environmentPassword}
                    onChange={(event) => setEnvironmentPassword(event.target.value)}
                    autoComplete="off"
                    name="shinq-environment-access-password"
                    spellCheck={false}
                    data-1p-ignore="true"
                    data-lpignore="true"
                  />
                  <button type="button" className="password-eye" aria-label={environmentPasswordVisible ? 'Hide environment password' : 'Show environment password'} onClick={() => setEnvironmentPasswordVisible((visible) => !visible)}>
                    <EyeIcon hidden={environmentPasswordVisible} />
                  </button>
                </span>
              </label>
            </div>
          )}
          <div className="form-section">
            <h2>Administrator account</h2>
            <label>Email<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" /></label>
            <label>
              Password
              <span className="password-shell">
                <input
                  type={adminPasswordVisible ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="off"
                  name="shinq-admin-access-password"
                  spellCheck={false}
                  data-1p-ignore="true"
                  data-lpignore="true"
                />
                <button type="button" className="password-eye" aria-label={adminPasswordVisible ? 'Hide administrator password' : 'Show administrator password'} onClick={() => setAdminPasswordVisible((visible) => !visible)}>
                  <EyeIcon hidden={adminPasswordVisible} />
                </button>
              </span>
            </label>
          </div>
          {authError && <div className="alert error">{authError}</div>}
          <button className="primary" disabled={busy}>{busy ? <ShinQLoader label="Signing in" size="button" /> : 'Open admin console'}</button>
        </form>
      </main>
    );
  }

  if (sessionLoading) {
    return <main className="admin-loader-page"><ShinQLoader iconOnly label="Loading administrator permissions" /></main>;
  }

  if (!adminSession) {
    return <main className="login-page"><div className="login-card"><AdminBrandMark /><p>{authError}</p><button className="secondary" onClick={signOut}>Return to sign in</button></div></main>;
  }

  if (!canSeeSection(section)) {
    const fallback = DASHBOARD_NAV_ITEMS.find((item) => canSeeSection(item.value))?.value;
    if (fallback) window.history.replaceState(null, '', dashboardSectionUrl(fallback, window.location));
    if (fallback && fallback !== section) window.setTimeout(() => setSection(fallback), 0);
    return <div className="admin-shell"><div className="workspace-empty">{fallback ? <ShinQLoader message="Opening your permitted admin workspace…" /> : 'No admin pages are assigned to this account.'}</div></div>;
  }

  return (
    <div className="admin-shell">
      <header className="admin-header">
        <div className="admin-header-brand"><AdminBrandMark compact /><div><p className="eyebrow">Administration</p><h1>Operations console</h1><p className="muted">Preview learning content, manage accounts, review billing, and inspect production data.</p></div></div>
        <div className="admin-header-actions"><ThemeModeButton colorMode={colorMode} onClick={toggleColorMode} /><button className="secondary" onClick={signOut}>Sign out</button></div>
      </header>

      <nav className="admin-nav" aria-label="Admin sections">
        {DASHBOARD_NAV_ITEMS.filter(({ value }) => canSeeSection(value)).map(({ value, label }) => (
          <button key={value} className={section === value ? 'active' : ''} onClick={() => selectSection(value)}>{label}</button>
        ))}
      </nav>

      {section !== 'assets' ? (
        <AdminWorkspace currentAdmin={adminSession} section={section} api={api} />
      ) : (
      <>
      <section className="metric-grid">
        <button type="button" className={`metric ${assetStatus === 'all' ? 'selected' : ''}`} aria-pressed={assetStatus === 'all'} onClick={() => selectAssetStatus('all')}><span>Results</span><strong>{summary.results}</strong><small>{pageMetrics.results} on this page</small></button>
        <button type="button" className={`metric ${assetStatus === 'complete' ? 'selected' : ''}`} aria-pressed={assetStatus === 'complete'} onClick={() => selectAssetStatus('complete')}><span>Complete</span><strong>{summary.complete}</strong><small>{pageMetrics.complete} on this page</small></button>
        <button type="button" className={`metric ${assetStatus === 'missing_image' ? 'selected' : ''}`} aria-pressed={assetStatus === 'missing_image'} onClick={() => selectAssetStatus('missing_image')}><span>Missing image</span><strong>{summary.missingImage}</strong><small>{pageMetrics.missingImage} on this page</small></button>
        <button type="button" className={`metric ${assetStatus === 'missing_audio' ? 'selected' : ''}`} aria-pressed={assetStatus === 'missing_audio'} onClick={() => selectAssetStatus('missing_audio')}><span>Missing audio</span><strong>{summary.missingAudio}</strong><small>{pageMetrics.missingAudio} on this page</small></button>
        <button type="button" className={`metric ${assetStatus === 'approved' ? 'selected' : ''}`} aria-pressed={assetStatus === 'approved'} onClick={() => selectAssetStatus('approved')}><span>Admin approved</span><strong>{summary.approved}</strong><small>{pageMetrics.approved} on this page</small></button>
      </section>

      <section className="toolbar">
        <input aria-label="Search vocabulary by beginning of English word" placeholder="Search beginning of English word" value={search} onChange={(event) => { setSearch(event.target.value); setCurrentPage(1); }} />
        <select value={level} onChange={(event) => { setLevel(event.target.value as Level | ''); setCurrentPage(1); }}>
          <option value="">All levels</option><option value="pre_basic">Pre-Basic</option><option value="beginner">Basic</option><option value="intermediate">Intermediate</option><option value="advanced">Advanced</option><option value="mastery">Mastery</option>
        </select>
        <select value={assetStatus} onChange={(event) => { setAssetStatus(event.target.value as AssetStatus); setCurrentPage(1); }}>
          <option value="all">All assets</option><option value="complete">Complete</option><option value="missing_image">Missing image</option><option value="missing_audio">Missing audio</option><option value="review">Needs review</option><option value="approved">Admin approved</option>
        </select>
        <button className="secondary" onClick={loadVocabulary}>Refresh</button>
      </section>

      {(notice || error) && (
        <div className={`notification-banner ${error ? 'error' : 'success'}`} role={error ? 'alert' : 'status'}>
          <span>{error || notice}</span>
          <button
            type="button"
            aria-label="Dismiss notification"
            onClick={() => { setNotice(''); setError(''); }}
          >
            ×
          </button>
        </div>
      )}
      {loading && !items.length ? <div className="asset-loader-only"><ShinQLoader iconOnly label="Loading vocabulary assets" /></div> : (
        <section className="review-list">
          {loading && <div className="refresh-indicator"><ShinQLoader message="Refreshing…" size="compact" /></div>}
          {items.map((item) => {
            const image = item.images?.[0];
            return (
              <article className="review-card" key={item.vocabId}>
                <div className="asset-column">
                  {image ? <img className="asset-image" src={image.mobile512Url || image.master1024Url} alt={`Vocabulary concept for ${item.englishWord}`} /> : <div className="asset-placeholder">No accepted image</div>}
                  <div className="asset-buttons">
                    {canWriteSection('assets') ? <button onClick={() => setReplacingImage(item)}>{image ? 'Replace image' : 'Generate image'}</button> : null}
                    {canWriteSection('assets') ? <>
                    <label className="upload-button">
                      Upload your image
                      <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = '';
                        void uploadImage(item, file);
                      }} />
                    </label>
                    <button onClick={() => void reuseImage(item)}>Reuse image</button>
                    {image && <button className="danger-link" onClick={() => removeAsset(item, 'image')}>Remove image</button>}
                    </> : null}
                  </div>
                </div>
                <div className="review-content">
                  <div className="word-row">
                    <div><h2>{item.englishWord}</h2><p className="pronunciation">{item.pronunciation} · {item.partOfSpeech}</p></div>
                    <div className="badges"><span>{item.proficiencyLevel}</span><span>Difficulty {item.difficulty}</span><span className={item.adminReviewStatus || 'pending'}>{item.adminReviewStatus || 'pending'}</span></div>
                  </div>
                  <div className="definition-grid definition-grid-three">
                    <div><small>Definition</small><p>{item.senses?.[0]?.definition || 'Missing'}</p></div>
                    <div><small>Japanese word</small><p lang="ja">{item.senses?.[0]?.japaneseTranslation || 'Missing'}</p></div>
                    <div><small>Japanese definition</small><p lang="ja">{item.senses?.[0]?.japaneseDefinition || 'Missing — add or verify a Japanese explanation'}</p></div>
                  </div>
                  <DictionaryVerification sense={item.senses?.[0]} />
                  <div className="audio-grid">
                    <div><strong>American pronunciation</strong>{item.audioAmericanUrl ? <audio controls preload="none" src={item.audioAmericanUrl} /> : <span>Missing</span>}<div className="asset-buttons"><button onClick={() => mutate(item, '/regenerate', { asset: 'american_audio' })}>{item.audioAmericanUrl ? 'Regenerate US' : 'Generate US'}</button>{item.audioAmericanUrl && <button className="danger-link" onClick={() => removeAsset(item, 'american_audio')}>Remove</button>}</div></div>
                    <div><strong>British pronunciation</strong>{item.audioBritishUrl ? <audio controls preload="none" src={item.audioBritishUrl} /> : <span>Missing</span>}<div className="asset-buttons"><button onClick={() => mutate(item, '/regenerate', { asset: 'british_audio' })}>{item.audioBritishUrl ? 'Regenerate UK' : 'Generate UK'}</button>{item.audioBritishUrl && <button className="danger-link" onClick={() => removeAsset(item, 'british_audio')}>Remove</button>}</div></div>
                  </div>
                  {(item.imageGenerationFailureReason || item.adminReviewNotes) && <div className="review-note">{item.adminReviewNotes || item.imageGenerationFailureReason}</div>}
                  {canWriteSection('assets') ? <div className="card-actions">
                    <button className="approve" onClick={() => mutate(item, '', { action: 'approve' })}>Approve</button>
                    <button onClick={() => setEditing(item)}>Edit content</button>
                    <button onClick={() => requestChanges(item)}>Request changes</button>
                    <button className="danger" onClick={() => removeAsset(item, 'entry')}>Remove entry</button>
                  </div> : null}
                </div>
              </article>
            );
          })}
          {!items.length && <div className="empty">No vocabulary matches these filters.</div>}
        </section>
      )}

      <footer className="pagination">
        <button
          className="secondary"
          disabled={currentPage <= 1}
          onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
        >
          Previous
        </button>
        <div className="pagination-jumps" aria-label="Page shortcuts">
          {[currentPage + 2, currentPage + 5, currentPage + 8]
            .filter((page) => page <= Math.ceil(total / pageSize))
            .map((page) => (
              <button className="pagination-page" key={page} onClick={() => setCurrentPage(page)}>
                {page}
              </button>
            ))}
        </div>
        <button
          className="secondary"
          disabled={currentPage >= Math.ceil(total / pageSize)}
          onClick={() => setCurrentPage((page) => page + 1)}
        >
          Next
        </button>
        <span className="pagination-summary">{total ? `${(currentPage - 1) * pageSize + 1}–${Math.min((currentPage - 1) * pageSize + items.length, total)} of ${total}` : '0 results'}</span>
      </footer>

      {editing && <EditDialog item={editing} onClose={() => setEditing(null)} onSave={async (changes) => { await mutate(editing, '', { action: 'edit', ...changes }); setEditing(null); }} />}
      {replacingImage && (
        <ImageGenerationDialog
          item={replacingImage}
          hasImage={Boolean(replacingImage.images?.length)}
          onClose={() => setReplacingImage(null)}
          onGenerate={async (notes, imageModel) => {
            await mutate(replacingImage, '/regenerate', { asset: 'image', notes, imageModel });
            setReplacingImage(null);
          }}
        />
      )}
      </>
      )}
    </div>
  );
}

type AdminApi = (path: string, init?: RequestInit) => Promise<unknown>;
type PreviewMode = CurriculumMode;

interface AdminOverview {
  users: { total: number; active: number; banned: number; learners: number };
  subscriptions: {
    free: number;
    paid: number;
    monthly: number;
    annual: number;
    monthlyRecurringRevenue: number;
    currency: string;
    note: string;
  };
  tables: Array<{ key: string; name: string; count: number }>;
}
interface AccountingOverview {
  period: { month: string; start: string; end: string; kind: 'actual' | 'current' | 'forecast' };
  customerBilling: { paidUsers: number; monthly: number; annual: number; monthlyRevenueMinor: number; currency: string };
  businessCosts: {
    awsCostUsd: number;
    forecastAwsCostUsd: number;
    services: Array<{ service: string; amount: number; currency: string }>;
    history: Array<{ date: string; amount: number; services: Array<{ service: string; amount: number }> }>;
    forecast: Array<{ start: string; end: string; amount: number }>;
    externalCosts: AccountingExpense[];
    note: string;
  };
  profitability: { operatingCostMinor: number | null; netProfitMinor: number | null; marginPercent: number | null; roiPercent: number | null; currency: string; exchangeRateConfigured: boolean };
}
interface AccountingExpense { expenseId: string; category: string; description: string; vendor?: string; amountMinor: number; currency: string; incurredAt: string; receiptKey?: string; receiptUrl?: string }
interface ReceiptExpenseSuggestion {
  category: string; description: string; vendor: string; amount: number; currency: string; incurredAt: string;
  sourceLanguage: 'ja' | 'en' | 'mixed' | 'unknown';
  confidence: { category: number; description: number; vendor: number; amount: number; incurredAt: number };
  reviewNotes: string[];
}

export interface AdminUser {
  accountId: string;
  cognitoUserId?: string;
  email: string;
  displayName?: string;
  accountType: string;
  accountStatus: 'active' | 'banned' | 'deletion_pending' | string;
  isEmailVerified: boolean;
  isPhoneVerified?: boolean;

  subscriptionPlan: string;
  effectiveSubscriptionPlan?: string;
  billingProvider?: 'Stripe' | 'Apple' | 'Google' | 'none' | string;
  subscriptionStatus?: string;
  subscriptionBillingInterval?: 'month' | 'year' | string;
  subscriptionUnitAmount?: number;
  subscriptionCurrency?: string;
  renewalDate?: string;

  // Quotas & Resets
  aiUsage?: { used: number; limit: number; resetAt: string };
  pronunciationUsage?: { used: number; limit: number; resetAt: string };
  translationUsage?: { used: number; limit: number; resetAt: string };
  examPracticeUsage?: { used: number; limit: number; resetAt: string };

  // Activity & Safety
  createdAt: string;
  updatedAt?: string;
  lastLoginAt?: string;
  lastActivityAt?: string;
  openSafetyIncidentsCount?: number;
  deletionStatus?: string;
  deletionRequestedAt?: string;
  exportStatus?: string;
  exportRequestedAt?: string;
  legalPolicyVersionAccepted?: string;
  legalPolicyAcceptedAt?: string;

  // Ban metadata
  bannedAt?: string;
  bannedBy?: string;
  banReason?: string;

  mobileNumber?: string;
  address?: string;
  learners: Array<{
    profileId: string;
    displayName: string;
    status?: string;
    proficiencyLevel: string;
    nativeLanguage?: string;
    ageGroup?: string;
    gender?: string;
    schoolName?: string;
    occupation?: string;
    pronunciationTrack?: string;
    placementCompletedAt?: string;
    latestExamReadinessAttempt?: {
      attemptId: string;
      track?: string;
      completed?: boolean;
      correct?: number;
      objectiveTotal?: number;
      completedAt?: string;
    };
    examReadinessAttempts?: Array<{
      attemptId: string;
      track?: string;
      completed?: boolean;
      correct?: number;
      objectiveTotal?: number;
      completedAt?: string;
      updatedAt?: string;
    }>;
    subscriptionUsage?: {
      features: Record<string, { used: number; limit: number | 'unlimited'; resetsAt: string }>;
    };
    domainPlacement?: Record<string, {
      level?: string;
      lastScore?: number;
      lastAssessmentAt?: string;
      beginnerExitPassed?: boolean;
      intermediateExitPassed?: boolean;
      advancedExitPassed?: boolean;
      masteryPassed?: boolean;
    }>;
  }>;
}

function AdminWorkspace({ section, api, currentAdmin }: { section: Exclude<DashboardSection, 'assets'>; api: AdminApi; currentAdmin: AdminRole }) {
  const sectionWritable = currentAdmin.isPrimary || currentAdmin.accessLevel === 'full' || (currentAdmin.accessLevel === 'write' && currentAdmin.sections.includes(section));
  const [overview, setOverview] = useState<AdminOverview>();
  const [accounting, setAccounting] = useState<AccountingOverview>();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [admins, setAdmins] = useState<AdminRole[]>([]);
  const [adminDraft, setAdminDraft] = useState<{ email: string; accessLevel: AdminAccessLevel; sections: AdminSection[]; permissions: AdminPermission[] }>({ email: '', accessLevel: 'read', sections: ['overview'], permissions: [] });
  const [previewMode, setPreviewMode] = useState<PreviewMode>(
    () => curriculumModeFromHash(window.location.hash),
  );
  const [search, setSearch] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('');
  const [userPlanFilter, setUserPlanFilter] = useState('');
  const [userProviderFilter, setUserProviderFilter] = useState('');
  const [userCursor, setUserCursor] = useState('');
  const [nextUserCursor, setNextUserCursor] = useState('');
  const [userCursorHistory, setUserCursorHistory] = useState<string[]>([]);
  const [selectedUser, setSelectedUser] = useState<AdminUser>();
  const [awsExpanded, setAwsExpanded] = useState(false);
  const [chartRange, setChartRange] = useState<'monthly' | 'yearly'>('monthly');
  const [expenseDraft, setExpenseDraft] = useState({ expenseId: '', category: '', description: '', vendor: '', amount: '', currency: 'jpy', incurredAt: new Date().toISOString().slice(0, 10) });
  const [receiptFile, setReceiptFile] = useState<File>();
  const [uploadedReceiptKey, setUploadedReceiptKey] = useState('');
  const [receiptSuggestion, setReceiptSuggestion] = useState<ReceiptExpenseSuggestion>();
  const [receiptReviewed, setReceiptReviewed] = useState(false);
  const [receiptAnalyzing, setReceiptAnalyzing] = useState(false);
  const [receiptAnalysisError, setReceiptAnalysisError] = useState('');
  const [selectedReceiptIds, setSelectedReceiptIds] = useState<string[]>([]);
  const [receiptReportStart, setReceiptReportStart] = useState(() => `${new Date().toISOString().slice(0, 7)}-01`);
  const [receiptReportEnd, setReceiptReportEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [receiptReportFormat, setReceiptReportFormat] = useState<'pdf' | 'xlsx'>('pdf');
  const [receiptReportBusy, setReceiptReportBusy] = useState(false);
  const [selectedAccountingMonth, setSelectedAccountingMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(true);
  const [workspaceError, setWorkspaceError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setWorkspaceError('');
    try {
      if (section === 'overview') {
        setOverview(await api('/admin/overview') as AdminOverview);
      } else if (section === 'billing') {
        setAccounting(await api(`/admin/accounting?month=${encodeURIComponent(selectedAccountingMonth)}`) as AccountingOverview);
      } else if (section === 'users') {
        const params = new URLSearchParams({ limit: '20' });
        if (search.trim()) params.set('search', search.trim());
        if (userStatusFilter) params.set('accountStatus', userStatusFilter);
        if (userPlanFilter) params.set('subscriptionPlan', userPlanFilter);
        if (userProviderFilter) params.set('billingProvider', userProviderFilter);
        if (userCursor) params.set('cursor', userCursor);
        const result = await api(`/admin/users?${params}`) as { users?: AdminUser[]; cursor?: string | null };
        const refreshedUsers = result.users ?? [];
        setUsers(refreshedUsers);
        setSelectedUser((current) => current
          ? refreshedUsers.find((candidate) => candidate.accountId === current.accountId)
          : undefined);
        setNextUserCursor(result.cursor ?? '');
      } else if (section === 'admins') {
        const result = await api('/admin/admins') as { admins?: AdminRole[] };
        setAdmins(result.admins ?? []);
      }
    } catch (requestError) {
      setWorkspaceError(requestError instanceof Error ? requestError.message : 'Unable to load admin data.');
    } finally {
      setLoading(false);
    }
  }, [api, search, section, selectedAccountingMonth, userCursor, userPlanFilter, userProviderFilter, userStatusFilter]);

  useEffect(() => {
    setUserCursor('');
    setUserCursorHistory([]);
  }, [search, userPlanFilter, userProviderFilter, userStatusFilter]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), section === 'users' ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [load, section]);

  useEffect(() => {
    if (section !== 'content') return;
    const syncPreviewMode = () => setPreviewMode(curriculumModeFromHash(window.location.hash));
    window.addEventListener('hashchange', syncPreviewMode);
    window.addEventListener('popstate', syncPreviewMode);
    return () => {
      window.removeEventListener('hashchange', syncPreviewMode);
      window.removeEventListener('popstate', syncPreviewMode);
    };
  }, [section]);

  if (loading && !(section === 'users' && users.length > 0)) {
    return <div className="workspace-empty"><ShinQLoader message={`Loading ${section}…`} /></div>;
  }
  if (workspaceError) return <div className="alert error">{workspaceError}</div>;

  if (section === 'overview' && overview) {
    return (
      <div className="workspace">
        <section className="metric-grid metric-grid-four">
          <Metric label="Accounts" value={overview.users.total} detail={`${overview.users.active} active`} />
          <Metric label="Learner profiles" value={overview.users.learners} detail="Across parent and adult accounts" />
          <Metric label="Paid subscriptions" value={overview.subscriptions.paid} detail={`${overview.subscriptions.free} free`} />
          <Metric label="Banned accounts" value={overview.users.banned} detail="Cognito sign-in disabled" />
        </section>
        <section className="panel">
          <div className="panel-heading"><div><p className="eyebrow">Live infrastructure</p><h2>Data tables</h2></div><button className="secondary" onClick={() => void load()}>Refresh</button></div>
          <div className="data-table-wrap"><table className="data-table"><thead><tr><th>Purpose</th><th>Physical table name</th><th>Records</th></tr></thead><tbody>
            {overview.tables.map((table) => <tr key={table.name}><td>{humanize(table.key)}</td><td><code>{table.name}</code></td><td>{table.count.toLocaleString()}</td></tr>)}
          </tbody></table></div>
        </section>
      </div>
    );
  }

  if (section === 'billing' && accounting) {
    const billing = accounting.customerBilling;
    const profit = accounting.profitability;
    const saveExpense = async () => {
      const reason = window.prompt(`Reason for ${expenseDraft.expenseId ? 'updating' : 'creating'} this financial record:`, '')?.trim();
      if (!reason) return;
      let receiptKey: string | undefined = uploadedReceiptKey || undefined;
      if (receiptFile && !receiptKey) {
        const upload = await api('/admin/accounting/expenses/receipt-upload', { method: 'POST', body: JSON.stringify({ fileName: receiptFile.name, contentType: receiptFile.type }) }) as { uploadUrl: string; receiptKey: string };
        const uploadResponse = await fetch(upload.uploadUrl, { method: 'PUT', headers: { 'Content-Type': receiptFile.type }, body: receiptFile });
        if (!uploadResponse.ok) throw new Error('Receipt upload failed.');
        receiptKey = upload.receiptKey;
      }
      const payload = { category: expenseDraft.category, description: expenseDraft.description, vendor: expenseDraft.vendor, amountMinor: Math.round(Number(expenseDraft.amount) * 100), currency: expenseDraft.currency, incurredAt: expenseDraft.incurredAt, receiptKey, reason };
      await api(expenseDraft.expenseId ? `/admin/accounting/expenses/${expenseDraft.expenseId}` : '/admin/accounting/expenses', { method: expenseDraft.expenseId ? 'PATCH' : 'POST', body: JSON.stringify(payload) });
      setExpenseDraft({ expenseId: '', category: '', description: '', vendor: '', amount: '', currency: 'jpy', incurredAt: new Date().toISOString().slice(0, 10) });
      setReceiptFile(undefined);
      setUploadedReceiptKey(''); setReceiptSuggestion(undefined); setReceiptReviewed(false); setReceiptAnalysisError('');
      setAccounting(await api(`/admin/accounting?month=${encodeURIComponent(selectedAccountingMonth)}`) as AccountingOverview);
    };
    const analyzeReceipt = async () => {
      if (!receiptFile) return;
      setReceiptAnalyzing(true); setReceiptAnalysisError(''); setReceiptReviewed(false);
      try {
        let receiptKey = uploadedReceiptKey;
        if (!receiptKey) {
          const upload = await api('/admin/accounting/expenses/receipt-upload', { method: 'POST', body: JSON.stringify({ fileName: receiptFile.name, contentType: receiptFile.type }) }) as { uploadUrl: string; receiptKey: string };
          const uploadResponse = await fetch(upload.uploadUrl, { method: 'PUT', headers: { 'Content-Type': receiptFile.type }, body: receiptFile });
          if (!uploadResponse.ok) throw new Error('Receipt upload failed.');
          receiptKey = upload.receiptKey; setUploadedReceiptKey(receiptKey);
        }
        const analysis = await api('/admin/accounting/expenses/receipt-analyze', { method: 'POST', body: JSON.stringify({ receiptKey }) }) as { suggestion: ReceiptExpenseSuggestion };
        const suggestion = analysis.suggestion;
        setReceiptSuggestion(suggestion);
        setExpenseDraft((draft) => ({ ...draft, category: suggestion.category, description: suggestion.description, vendor: suggestion.vendor, amount: String(suggestion.amount), currency: suggestion.currency === 'other' ? draft.currency : suggestion.currency, incurredAt: suggestion.incurredAt }));
      } catch (analysisError) {
        setReceiptAnalysisError(analysisError instanceof Error ? analysisError.message : 'Receipt analysis failed. Enter the expense manually.');
      } finally { setReceiptAnalyzing(false); }
    };
    const downloadReceiptReport = async () => {
      setReceiptReportBusy(true); setWorkspaceError('');
      try {
        const selected = accounting.businessCosts.externalCosts.filter((expense) => expense.receiptKey && selectedReceiptIds.includes(expense.expenseId));
        const expenses = selected.length ? selected : ((await api(`/admin/accounting/expenses?start=${encodeURIComponent(receiptReportStart)}&end=${encodeURIComponent(receiptReportEnd)}`)) as { expenses: AccountingExpense[] }).expenses;
        if (!expenses.length) throw new Error('No receipts match this selection or date range.');
        const fileBase = `shin-q-receipts-${selected.length ? 'selected' : `${receiptReportStart}-to-${receiptReportEnd}`}`;
        if (receiptReportFormat === 'xlsx') {
          const rows = expenses.map((expense) => ({ Date: expense.incurredAt, Category: expense.category, Description: expense.description, Vendor: expense.vendor ?? '', Amount: expense.amountMinor / 100, Currency: expense.currency.toUpperCase(), 'Receipt file': expense.receiptKey ?? '' }));
          const workbook = new ExcelJS.Workbook();
          const worksheet = workbook.addWorksheet('Receipts');
          worksheet.columns = Object.keys(rows[0]).map((header) => ({ header, key: header, width: header === 'Description' || header === 'Receipt file' ? 48 : header === 'Category' || header === 'Vendor' ? 26 : 14 }));
          worksheet.addRows(rows);
          worksheet.getRow(1).font = { bold: true };
          const buffer = await workbook.xlsx.writeBuffer();
          downloadBlob(new Blob([buffer as unknown as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${fileBase}.xlsx`);
        } else {
          const document = new jsPDF({ unit: 'mm', format: 'a4' });
          document.setFontSize(16); document.text('Shin Q Business Receipt Report', 14, 16);
          document.setFontSize(9); document.text(`Generated ${new Date().toISOString().slice(0, 10)} | ${expenses.length} receipt(s)`, 14, 23);
          let y = 32;
          expenses.forEach((expense, index) => {
            if (y > 270) { document.addPage(); y = 16; }
            document.setFontSize(11); document.text(`${index + 1}. ${expense.incurredAt} | ${expense.category}`, 14, y); y += 6;
            document.setFontSize(9);
            const details = `Vendor: ${pdfSafeText(expense.vendor || 'Not recorded')} | Amount: ${money(expense.amountMinor, expense.currency)}`;
            document.text(details, 18, y); y += 5;
            const descriptionLines = document.splitTextToSize(`Description: ${pdfSafeText(expense.description)}`, 174);
            document.text(descriptionLines, 18, y); y += descriptionLines.length * 4 + 3;
            document.setDrawColor(210); document.line(14, y, 196, y); y += 6;
          });
          document.save(`${fileBase}.pdf`);
        }
      } catch (reportError) {
        setWorkspaceError(reportError instanceof Error ? reportError.message : 'Unable to create receipt report.');
      } finally { setReceiptReportBusy(false); }
    };
    const chartPoints = accounting.period.kind === 'forecast' && chartRange === 'monthly'
      ? [{ date: `${accounting.period.month}-01`, amount: accounting.businessCosts.forecastAwsCostUsd, services: [{ service: 'AWS forecast', amount: accounting.businessCosts.forecastAwsCostUsd }] }]
      : aggregateAccountingHistory(accounting.businessCosts.history, chartRange, accounting.period.month);
    const chartMax = Math.max(0.01, ...chartPoints.map((point) => point.amount));
    const chartServices = Array.from(new Set(chartPoints.flatMap((point) => point.services.map((service) => service.service)))).slice(0, 12);
    return (
      <div className="workspace">
        <div className="panel-heading billing-period-heading">
          <div><p className="eyebrow">Owner accounting dashboard</p><h2>Revenue, costs, and profitability</h2><p className="muted">{accountingMonthLabel(accounting.period.month)} · {accounting.period.kind === 'forecast' ? 'Forecast period' : accounting.period.kind === 'current' ? 'Current month to date' : 'Historical month'}</p></div>
          <div className="billing-month-picker">
            <button aria-label="Previous month" className="secondary" onClick={() => setSelectedAccountingMonth((month) => shiftAccountingMonth(month, -1))}>‹</button>
            <label>Report month<input aria-label="Billing report month" type="month" value={selectedAccountingMonth} onChange={(event) => { if (event.target.value) setSelectedAccountingMonth(event.target.value); }} /></label>
            <button aria-label="Next month" className="secondary" onClick={() => setSelectedAccountingMonth((month) => shiftAccountingMonth(month, 1))}>›</button>
            <button className="secondary" onClick={() => setSelectedAccountingMonth(new Date().toISOString().slice(0, 7))}>Current month</button>
          </div>
        </div>
        <section className="metric-grid metric-grid-four">
          <Metric label="Monthly plans" value={billing.monthly} detail="Active paid accounts" />
          <Metric label="Annual plans" value={billing.annual} detail="Active paid accounts" />
          <Metric label="Estimated MRR" value={money(billing.monthlyRevenueMinor, billing.currency)} detail="Annual plans normalized monthly" />
          <Metric label="Net profit" value={profit.netProfitMinor === null ? 'Needs FX rate' : money(profit.netProfitMinor, profit.currency)} detail="Revenue minus recorded costs" />
        </section>
        <BillingOperationsPanel api={api} />
        <section className="user-record-grid">
          <div className="panel"><p className="eyebrow">Customer billing</p><h2>Subscription revenue</h2><BillingBar label="Monthly customers" value={billing.monthly} total={Math.max(1, billing.paidUsers)} /><BillingBar label="Annual customers" value={billing.annual} total={Math.max(1, billing.paidUsers)} /></div>
          <div className="panel"><p className="eyebrow">Profitability</p><h2>{profit.roiPercent === null ? 'ROI pending' : `${profit.roiPercent.toFixed(1)}% ROI`}</h2><p>Margin: {profit.marginPercent === null ? 'Needs currency conversion' : `${profit.marginPercent.toFixed(1)}%`}</p><p className="muted">{profit.exchangeRateConfigured ? 'AWS costs converted to revenue currency.' : 'Configure ACCOUNTING_USD_TO_REVENUE_RATE before calculating ROI across currencies.'}</p></div>
        </section>
        <section className="panel">
          <div className="panel-heading"><div><p className="eyebrow">Business billing</p><h2>{accounting.period.kind === 'forecast' ? 'Forecast AWS cost' : accounting.period.kind === 'current' ? 'AWS month-to-date' : 'AWS monthly cost'} · ${(accounting.period.kind === 'forecast' ? accounting.businessCosts.forecastAwsCostUsd : accounting.businessCosts.awsCostUsd).toFixed(2)}</h2></div><button className="secondary" onClick={() => setAwsExpanded((value) => !value)}>{awsExpanded ? 'Hide details' : 'View details'}</button></div>
          {awsExpanded && <div className="data-table-wrap"><table className="data-table"><thead><tr><th>AWS service</th><th>Cost</th></tr></thead><tbody>{accounting.period.kind === 'forecast' ? <tr><td>All AWS services · forecast</td><td>{accounting.businessCosts.forecastAwsCostUsd.toFixed(2)} USD</td></tr> : accounting.businessCosts.services.map((service) => <tr key={service.service}><td>{service.service}</td><td>{service.amount.toFixed(2)} {service.currency}</td></tr>)}</tbody></table></div>}
          <p className="muted billing-note">{accounting.businessCosts.note}</p>
        </section>
        <section className="panel">
          <div className="panel-heading"><div><p className="eyebrow">Cost and ROI trends</p><h2>{accounting.period.kind === 'forecast' ? `AWS forecast for ${accountingMonthLabel(accounting.period.month)}` : chartRange === 'monthly' ? `Daily AWS costs in ${accountingMonthLabel(accounting.period.month)}` : `Monthly AWS costs in ${accounting.period.month.slice(0, 4)}`}</h2></div><select value={chartRange} onChange={(event) => setChartRange(event.target.value as typeof chartRange)}><option value="monthly">Monthly report</option><option value="yearly">Yearly report</option></select></div>
          <div className="accounting-chart-shell"><div className="accounting-y-axis"><span>${chartMax.toFixed(2)}</span><span>${(chartMax * .75).toFixed(2)}</span><span>${(chartMax * .5).toFixed(2)}</span><span>${(chartMax * .25).toFixed(2)}</span><span>$0</span></div><div className="accounting-chart">{chartPoints.map((point) => <div className="accounting-column" key={point.date}><div className="accounting-stack" title={`${point.date}: $${point.amount.toFixed(2)}`} style={{ height: `${Math.max(3, point.amount / chartMax * 100)}%` }}>{point.services.map((service) => <i key={service.service} title={`${point.date} · ${service.service}: $${service.amount.toFixed(2)}`} style={{ background: accountingServiceColor(service.service), height: `${service.amount / Math.max(point.amount, .0001) * 100}%` }} />)}</div><small>{accountingDateLabel(point.date, chartRange)}</small></div>)}</div></div>
          <div className="accounting-legend">{chartServices.map((service) => <span key={service}><i style={{ background: accountingServiceColor(service) }} />{service}</span>)}</div>
          <p className="muted">AWS forecast: {accounting.businessCosts.forecast.length ? accounting.businessCosts.forecast.map((item) => `$${item.amount.toFixed(2)}`).join(' · ') : 'Not available for this period'} · Subscription estimate uses the current MRR of {money(billing.monthlyRevenueMinor, billing.currency)}.</p>
        </section>
        <section className="panel">
          <p className="eyebrow">Manual operating expenses</p><h2>Add business expense</h2>
          {sectionWritable ? <><div className="expense-form"><input placeholder="Category (business trip, client meal…)" value={expenseDraft.category} onChange={(e) => { setExpenseDraft({ ...expenseDraft, category: e.target.value }); setReceiptReviewed(false); }} /><input placeholder="Description" value={expenseDraft.description} onChange={(e) => { setExpenseDraft({ ...expenseDraft, description: e.target.value }); setReceiptReviewed(false); }} /><input placeholder="Vendor" value={expenseDraft.vendor} onChange={(e) => { setExpenseDraft({ ...expenseDraft, vendor: e.target.value }); setReceiptReviewed(false); }} /><input type="number" min="0" step=".01" placeholder="Amount" value={expenseDraft.amount} onChange={(e) => { setExpenseDraft({ ...expenseDraft, amount: e.target.value }); setReceiptReviewed(false); }} /><select value={expenseDraft.currency} onChange={(e) => { setExpenseDraft({ ...expenseDraft, currency: e.target.value }); setReceiptReviewed(false); }}><option value="jpy">JPY</option><option value="usd">USD</option><option value="eur">EUR</option><option value="gbp">GBP</option></select><input type="date" value={expenseDraft.incurredAt} onChange={(e) => { setExpenseDraft({ ...expenseDraft, incurredAt: e.target.value }); setReceiptReviewed(false); }} /><label className="secondary receipt-input">{receiptFile ? receiptFile.name : 'Add receipt'}<input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(e) => { setReceiptFile(e.target.files?.[0]); setUploadedReceiptKey(''); setReceiptSuggestion(undefined); setReceiptReviewed(false); setReceiptAnalysisError(''); }} /></label><button className="secondary" disabled={!receiptFile || receiptAnalyzing} onClick={() => void analyzeReceipt()}>{receiptAnalyzing ? 'Reading receipt…' : 'Auto-fill from receipt'}</button></div>{receiptAnalysisError ? <div className="alert error">{receiptAnalysisError}</div> : null}{receiptSuggestion ? <div className="receipt-review"><strong>Review the extracted receipt</strong><p>Detected language: {receiptSuggestion.sourceLanguage === 'ja' ? 'Japanese' : receiptSuggestion.sourceLanguage === 'en' ? 'English' : receiptSuggestion.sourceLanguage}. Check every field before approval.</p><div className="receipt-confidence">{Object.entries(receiptSuggestion.confidence).map(([field, confidence]) => <span className={confidence < .75 ? 'low' : ''} key={field}>{field}: {Math.round(confidence * 100)}%</span>)}</div>{receiptSuggestion.reviewNotes.length ? <ul>{receiptSuggestion.reviewNotes.map((note) => <li key={note}>{note}</li>)}</ul> : null}<label className="receipt-approval"><input type="checkbox" checked={receiptReviewed} onChange={(event) => setReceiptReviewed(event.target.checked)} /> I checked the category, description, vendor, amount, currency, and date.</label></div> : null}<button disabled={Boolean(receiptSuggestion) && !receiptReviewed} onClick={() => void saveExpense()}>{expenseDraft.expenseId ? 'Save changes' : receiptSuggestion ? 'Approve and add expense' : 'Add expense'}</button></> : <p className="muted">Read-only billing access. Expense changes are disabled.</p>}
          <div className="data-table-wrap"><table className="data-table"><thead><tr><th><input aria-label="Select all receipts on this page" type="checkbox" checked={Boolean(accounting.businessCosts.externalCosts.filter((expense) => expense.receiptKey).length) && accounting.businessCosts.externalCosts.filter((expense) => expense.receiptKey).every((expense) => selectedReceiptIds.includes(expense.expenseId))} onChange={(event) => setSelectedReceiptIds(event.target.checked ? accounting.businessCosts.externalCosts.filter((expense) => expense.receiptKey).map((expense) => expense.expenseId) : [])} /></th><th>Date</th><th>Category</th><th>Description</th><th>Amount</th><th>Receipt</th>{sectionWritable ? <th /> : null}</tr></thead><tbody>{accounting.businessCosts.externalCosts.map((expense) => <tr key={expense.expenseId}><td><input aria-label={`Select receipt for ${expense.description}`} disabled={!expense.receiptKey} type="checkbox" checked={selectedReceiptIds.includes(expense.expenseId)} onChange={(event) => setSelectedReceiptIds((ids) => event.target.checked ? [...new Set([...ids, expense.expenseId])] : ids.filter((id) => id !== expense.expenseId))} /></td><td>{expense.incurredAt}</td><td>{expense.category}</td><td>{expense.description}<small>{expense.vendor}</small></td><td>{money(expense.amountMinor, expense.currency)}</td><td>{expense.receiptUrl ? <><a href={expense.receiptUrl} target="_blank" rel="noreferrer">View</a> · <a download href={expense.receiptUrl}>Download</a></> : 'No receipt'}</td>{sectionWritable ? <td><button className="secondary" onClick={() => setExpenseDraft({ expenseId: expense.expenseId, category: expense.category, description: expense.description, vendor: expense.vendor || '', amount: String(expense.amountMinor / 100), currency: expense.currency, incurredAt: expense.incurredAt })}>Edit</button><button className="danger" onClick={async () => { if (!window.confirm('Delete this expense?')) return; const reason = window.prompt('Reason for deleting this financial record:', '')?.trim(); if (!reason) return; await api(`/admin/accounting/expenses/${expense.expenseId}`, { method: 'DELETE', body: JSON.stringify({ reason }) }); setAccounting(await api(`/admin/accounting?month=${encodeURIComponent(selectedAccountingMonth)}`) as AccountingOverview); }}>Delete</button></td> : null}</tr>)}</tbody></table></div>
          <div className="receipt-report"><div><p className="eyebrow">Receipt downloads</p><h3>Download receipt report</h3><p className="muted">Checked receipts take priority. With no rows checked, the selected date range is used.</p></div><div className="receipt-report-controls"><label>From<input type="date" value={receiptReportStart} onChange={(event) => setReceiptReportStart(event.target.value)} /></label><label>To<input type="date" value={receiptReportEnd} onChange={(event) => setReceiptReportEnd(event.target.value)} /></label><button className="secondary" onClick={() => { const month = new Date().toISOString().slice(0, 7); setReceiptReportStart(`${month}-01`); setReceiptReportEnd(new Date().toISOString().slice(0, 10)); }}>This month</button><button className="secondary" onClick={() => { const month = shiftAccountingMonth(new Date().toISOString().slice(0, 7), -1); setReceiptReportStart(`${month}-01`); setReceiptReportEnd(lastDayOfMonth(month)); }}>Previous month</button><select value={receiptReportFormat} onChange={(event) => setReceiptReportFormat(event.target.value as 'pdf' | 'xlsx')}><option value="pdf">PDF report</option><option value="xlsx">Excel report</option></select><button disabled={receiptReportBusy || (!selectedReceiptIds.length && (!receiptReportStart || !receiptReportEnd))} onClick={() => void downloadReceiptReport()}>{receiptReportBusy ? 'Preparing…' : `Download ${selectedReceiptIds.length ? `${selectedReceiptIds.length} selected` : 'date range'}`}</button></div></div>
        </section>
      </div>
    );
  }

  if (section === 'users') {
    const moderate = async (targetUser: AdminUser) => {
      const banning = targetUser.accountStatus !== 'banned';
      const reason = window.prompt(`Mandatory reason for ${banning ? 'banning' : 'unbanning'} ${targetUser.email}:`, '')?.trim();
      if (!reason) return;
      await api(banning ? '/admin/users/ban' : '/admin/users/unban', {
        method: 'POST',
        body: JSON.stringify({ accountId: targetUser.accountId, reason }),
      });
      await load();
    };
    return (
      <div className="workspace">
        <section className="toolbar user-toolbar" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            placeholder="Exact account ID or email"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            style={{ flex: 1, minWidth: '220px' }}
          />
          <select aria-label="Account status" value={userStatusFilter} onChange={(event) => setUserStatusFilter(event.target.value)}><option value="">All statuses</option><option value="active">Active</option><option value="banned">Banned</option><option value="deletion_pending">Deletion pending</option></select>
          <select aria-label="Subscription plan" value={userPlanFilter} onChange={(event) => setUserPlanFilter(event.target.value)}><option value="">All plans</option><option value="free">Free</option><option value="standard">Standard</option><option value="premium">Premium</option><option value="family">Family</option></select>
          <select aria-label="Billing provider" value={userProviderFilter} onChange={(event) => setUserProviderFilter(event.target.value)}><option value="">All providers</option><option value="none">None</option><option value="stripe">Stripe</option><option value="apple">Apple</option><option value="google">Google</option></select>
          <button className="secondary" type="button" disabled={loading} onClick={() => void load()}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </section>

        {selectedUser && (
          <AdminUserRecord
            user={selectedUser}
            api={api}
            canReveal={currentAdmin.isPrimary || currentAdmin.permissions?.includes('users.sensitive.reveal')}
            isCurrentAdmin={currentAdmin.adminId === selectedUser.accountId}
            writable={sectionWritable}
            onClose={() => setSelectedUser(undefined)}
            onRefresh={load}
          />
        )}

        <section className="panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Account directory</p>
              <h2>{users.length} users</h2>
            </div>
            <p className="muted">Passwords, tokens, and full payment secrets are strictly protected and never stored or exposed.</p>
          </div>
          <div className="data-table-wrap">
            <table className="data-table user-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>IDs</th>
                  <th>Profiles</th>
                  <th>Subscription</th>
                  <th>Activity</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr className="clickable-user-row" key={user.accountId} onClick={() => setSelectedUser(user)}>
                    <td>
                      <strong>{currentAdmin.adminId === user.accountId ? 'Your account' : user.displayName || 'No display name'}</strong>
                      <span>{user.email}</span>
                      <small>{user.accountType} · {user.isEmailVerified ? 'verified' : 'unverified'}</small>
                    </td>
                    <td>
                      <code>{user.accountId}</code>
                      <small>Cognito: {user.cognitoUserId}</small>
                    </td>
                    <td>
                      {user.learners.length ? (
                        user.learners.map((learner) => (
                          <span key={learner.profileId}>{learner.displayName} · {humanize(learner.proficiencyLevel)}</span>
                        ))
                      ) : (
                        <span>None</span>
                      )}
                    </td>
                    <td>
                      <strong>{humanize(user.subscriptionPlan)}</strong>
                      <span>{user.billingProvider ? `${user.billingProvider} · ` : ''}{user.subscriptionBillingInterval ? `${user.subscriptionBillingInterval}ly` : user.subscriptionStatus || '—'}</span>
                    </td>
                    <td>
                      <span>Joined {date(user.createdAt)}</span>
                      <small>Last login {date(user.lastLoginAt)}</small>
                    </td>
                    <td>
                      <span className={`status-pill ${user.accountStatus}`}>{user.accountStatus}</span>
                      {user.banReason && <small>{user.banReason}</small>}
                    </td>
                    <td>
                      {currentAdmin.adminId === user.accountId ? (
                        <span className="status-pill protected">Protected</span>
                      ) : sectionWritable ? (
                        <button
                          className={user.accountStatus === 'banned' ? 'secondary' : 'danger'}
                          onClick={(event) => {
                            event.stopPropagation();
                            void moderate(user);
                          }}
                        >
                          {user.accountStatus === 'banned' ? 'Unban' : 'Ban'}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
                {!users.length ? <tr><td colSpan={7}>No accounts match the current search and filters.</td></tr> : null}
              </tbody>
            </table>
          </div>
          <div className="pagination"><button className="secondary" disabled={!userCursorHistory.length} onClick={() => { const history = [...userCursorHistory]; const previous = history.pop() ?? ''; setUserCursorHistory(history); setUserCursor(previous); }}>Previous</button><span>{users.length} accounts on this page</span><button className="secondary" disabled={!nextUserCursor} onClick={() => { setUserCursorHistory((history) => [...history, userCursor]); setUserCursor(nextUserCursor); }}>Next</button></div>
        </section>
      </div>
    );
  }

  if (section === 'admins') {
    const writable = currentAdmin.isPrimary || currentAdmin.accessLevel === 'full' || (currentAdmin.accessLevel === 'write' && currentAdmin.sections.includes('admins'));
    const toggleDraftSection = (target: AdminSection) => setAdminDraft((current) => ({
      ...current,
      sections: current.sections.includes(target) ? current.sections.filter((item) => item !== target) : [...current.sections, target],
    }));
    const toggleDraftPermission = (target: AdminPermission) => setAdminDraft((current) => ({
      ...current,
      permissions: current.permissions.includes(target) ? current.permissions.filter((item) => item !== target) : [...current.permissions, target],
    }));
    const addAdmin = async () => {
      const reason = window.prompt('Reason for granting administrator access:', '')?.trim();
      if (!reason) return;
      await api('/admin/admins', { method: 'POST', body: JSON.stringify({ ...adminDraft, reason }) });
      setAdminDraft({ email: '', accessLevel: 'read', sections: ['overview'], permissions: [] });
      await load();
    };
    const updateAdmin = async (admin: AdminRole, accessLevel: AdminAccessLevel, sections: AdminSection[], permissions: AdminPermission[]) => {
      const reason = window.prompt(`Reason for changing ${admin.email}'s permissions:`, '')?.trim();
      if (!reason) return;
      await api(`/admin/admins/${encodeURIComponent(admin.adminId)}`, { method: 'PATCH', body: JSON.stringify({ accessLevel, sections, permissions, reason }) });
      await load();
    };
    const revokeAdmin = async (admin: AdminRole) => {
      if (!window.confirm(`Remove administrator access from ${admin.email}? Their Shin Q learner account and data will remain.`)) return;
      const reason = window.prompt('Reason for revoking administrator access:', '')?.trim();
      if (!reason) return;
      await api(`/admin/admins/${encodeURIComponent(admin.adminId)}`, { method: 'DELETE', body: JSON.stringify({ reason }) });
      await load();
    };
    return (
      <div className="workspace">
        <section className="panel">
          <div className="panel-heading"><div><p className="eyebrow">Protected administration</p><h2>Admin management</h2></div><p className="muted">The primary administrator is immutable. Removing an admin only revokes console access; it does not delete their learner account.</p></div>
          {writable ? <div className="admin-create-form">
            <label>Existing Shin Q account email<input type="email" placeholder="admin@example.com" value={adminDraft.email} onChange={(event) => setAdminDraft({ ...adminDraft, email: event.target.value })} /></label>
            <label>Access level<select value={adminDraft.accessLevel} onChange={(event) => setAdminDraft({ ...adminDraft, accessLevel: event.target.value as AdminAccessLevel })}><option value="read">Read access</option><option value="write">Write access</option><option value="full">Full access</option></select></label>
            <fieldset disabled={adminDraft.accessLevel === 'full'}><legend>Visible pages</legend><div className="permission-grid">{DASHBOARD_NAV_ITEMS.map((item) => <label key={item.value}><input type="checkbox" checked={adminDraft.accessLevel === 'full' || adminDraft.sections.includes(item.value)} onChange={() => toggleDraftSection(item.value)} />{item.label}</label>)}</div></fieldset>
            <fieldset><legend>Sensitive action grants</legend><div className="permission-grid">{ADMIN_PERMISSION_OPTIONS.map((permission) => <label key={permission.value}><input type="checkbox" checked={adminDraft.permissions.includes(permission.value)} onChange={() => toggleDraftPermission(permission.value)} />{permission.label}</label>)}</div></fieldset>
            <button className="primary" disabled={!adminDraft.email.trim() || (adminDraft.accessLevel !== 'full' && !adminDraft.sections.length)} onClick={() => void addAdmin()}>Add administrator</button>
          </div> : <div className="alert">Your role can view administrators but cannot change them.</div>}
          <div className="admin-role-list">
            {admins.map((admin) => <AdminRoleCard key={admin.adminId} admin={admin} currentAdminId={currentAdmin.adminId} writable={writable} onChange={updateAdmin} onRevoke={revokeAdmin} />)}
          </div>
        </section>
      </div>
    );
  }

  if (section === 'postal') {
    return <PostalCodeTesterView />;
  }

  if (section === 'security') {
    return <SecurityThreatReport
      api={api}
      canInvestigate={currentAdmin.isPrimary || currentAdmin.permissions?.includes('security.investigate')}
      canResolve={currentAdmin.isPrimary || currentAdmin.permissions?.includes('security.resolve')}
      canBlock={currentAdmin.isPrimary || currentAdmin.permissions?.includes('security.block')}
      canUnblock={currentAdmin.isPrimary || currentAdmin.permissions?.includes('security.unblock')}
    />;
  }

  if (section === 'safety') {
    return (
      <SafetyWorkspace
        api={api}
        canDecrypt={currentAdmin.isPrimary || currentAdmin.permissions?.includes('safety.decrypt')}
        currentAdminId={currentAdmin.adminId}
        isPrimary={currentAdmin.isPrimary}
      />
    );
  }

  if (section === 'privacy') {
    return <PrivacyWorkspace api={api} />;
  }

  if (section === 'legal') {
    return <LegalContentWorkspace api={api} writable={sectionWritable} />;
  }

  if (section === 'operations') {
    return <SystemOperationsWorkspace api={api} />;
  }

  if (section === 'support') {
    return <SupportWorkspace api={api} writable={sectionWritable} />;
  }

  if (section === 'audit') {
    return <AuditWorkspace api={api} />;
  }

  if (section === 'animations') {
    return <AnimationsAndSoundsWorkspace api={api} writable={sectionWritable} />;
  }

  return (
    <div className="workspace">
      <ContentOperationsPanel api={api} writable={sectionWritable} />
      <section className="preview-tabs">
        {CONTENT_TOOL_GROUPS.map((group) => (
          <div className="preview-tool-layer" key={group.title}>
            <h3>{group.title}</h3>
            <div className="preview-tool-buttons">
              {group.tools.map(([mode, label]) => <button className={previewMode === mode ? 'active' : ''} key={mode} onClick={() => {
                setPreviewMode(mode);
                window.history.pushState(null, '', `#content/${mode}`);
              }}>{label}</button>)}
            </div>
          </div>
        ))}
      </section>
      <CurriculumContentPreview
        api={api}
        key={previewMode}
        mode={previewMode}
        writable={currentAdmin.isPrimary || currentAdmin.accessLevel === 'full' || (currentAdmin.accessLevel === 'write' && currentAdmin.sections.includes('content'))}
      />
    </div>
  );
}

function AdminRoleCard({ admin, currentAdminId, writable, onChange, onRevoke }: {
  admin: AdminRole;
  currentAdminId: string;
  writable: boolean;
  onChange: (admin: AdminRole, accessLevel: AdminAccessLevel, sections: AdminSection[], permissions: AdminPermission[]) => Promise<void>;
  onRevoke: (admin: AdminRole) => Promise<void>;
}) {
  const [accessLevel, setAccessLevel] = useState(admin.accessLevel);
  const [sections, setSections] = useState<AdminSection[]>(admin.sections);
  const [permissions, setPermissions] = useState<AdminPermission[]>(admin.permissions ?? []);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setAccessLevel(admin.accessLevel); setSections(admin.sections); setPermissions(admin.permissions ?? []); }, [admin]);
  const immutable = admin.isPrimary || admin.adminId === currentAdminId || !writable;
  const toggleSection = (target: AdminSection) => setSections((current) => current.includes(target) ? current.filter((item) => item !== target) : [...current, target]);
  const togglePermission = (target: AdminPermission) => setPermissions((current) => current.includes(target) ? current.filter((item) => item !== target) : [...current, target]);
  const save = async () => {
    setSaving(true);
    try { await onChange(admin, accessLevel, sections, permissions); } finally { setSaving(false); }
  };
  return <article className={`admin-role-card ${admin.isPrimary ? 'primary-admin-card' : ''}`}>
    <div className="admin-role-heading">
      <div><p className="eyebrow">{admin.isPrimary ? 'Primary administrator' : 'Delegated administrator'}</p><h3>{admin.email}</h3><small>{admin.status || 'active'} · ID {admin.adminId}</small></div>
      {admin.isPrimary ? <span className="status-pill">Protected</span> : null}
    </div>
    <label>Access level<select disabled={immutable} value={accessLevel} onChange={(event) => setAccessLevel(event.target.value as AdminAccessLevel)}><option value="read">Read access</option><option value="write">Write access</option><option value="full">Full access</option></select></label>
    <fieldset disabled={immutable || accessLevel === 'full'}><legend>Visible pages</legend><div className="permission-grid">{DASHBOARD_NAV_ITEMS.map((item) => <label key={item.value}><input type="checkbox" checked={accessLevel === 'full' || sections.includes(item.value)} onChange={() => toggleSection(item.value)} />{item.label}</label>)}</div></fieldset>
    <fieldset disabled={immutable}><legend>Sensitive action grants</legend><div className="permission-grid">{ADMIN_PERMISSION_OPTIONS.map((permission) => <label key={permission.value}><input type="checkbox" checked={permissions.includes(permission.value)} onChange={() => togglePermission(permission.value)} />{permission.label}</label>)}</div></fieldset>
    <div className="card-actions">
      {admin.isPrimary ? <p className="muted">This account cannot be edited, demoted, disabled, or removed by another administrator.</p> : admin.adminId === currentAdminId ? <p className="muted">You cannot change or revoke your own role.</p> : writable ? <><button className="primary" disabled={saving || (accessLevel !== 'full' && !sections.length)} onClick={() => void save()}>{saving ? 'Saving…' : 'Save permissions'}</button><button className="danger" onClick={() => void onRevoke(admin)}>Remove admin access</button></> : null}
    </div>
  </article>;
}

const ENGLISH_LEVEL_DOMAINS = ['reading', 'listening', 'writing', 'speaking', 'pronunciation', 'grammar', 'vocabulary'] as const;

function AdminUserRecord({
  user,
  api,
  canReveal,
  isCurrentAdmin,
  writable,
  onClose,
  onRefresh,
}: {
  user: AdminUser;
  api: AdminApi;
  canReveal: boolean;
  isCurrentAdmin: boolean;
  writable: boolean;
  onClose: () => void;
  onRefresh: () => Promise<void>;
}) {
  const [unmaskedUser, setUnmaskedUser] = useState<AdminUser | null>(null);
  const [revealModalOpen, setRevealModalOpen] = useState(false);
  const [actionModalType, setActionModalType] = useState<'ban' | 'unban' | 'revoke' | 'resend' | 'correct' | 'reset' | null>(null);
  const [auditReason, setAuditReason] = useState('');
  const [resetConfirmation, setResetConfirmation] = useState('');
  const [accountIdCopied, setAccountIdCopied] = useState(false);
  const [correctNameDraft, setCorrectNameDraft] = useState(user.displayName || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const displayUser = unmaskedUser || user;

  const handleReveal = async () => {
    if (!auditReason.trim()) {
      setErrorMsg('Mandatory reason is required for security audit log.');
      return;
    }
    setIsSubmitting(true);
    setErrorMsg('');
    try {
      const res = await api('/admin/users/reveal', {
        method: 'POST',
        body: JSON.stringify({ accountId: user.accountId, reason: auditReason }),
      }) as { user?: Partial<AdminUser> };
      if (res.user) {
        const revealedLearners = res.user.learners ?? [];
        setUnmaskedUser({
          ...user,
          ...res.user,
          learners: user.learners.map((learner) => ({
            ...learner,
            ...(revealedLearners.find((candidate) => candidate.profileId === learner.profileId) ?? {}),
          })),
        });
        setRevealModalOpen(false);
        setAuditReason('');
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Unable to reveal sensitive PII data.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExecuteSupportAction = async () => {
    if (!actionModalType) return;
    if (!auditReason.trim()) {
      setErrorMsg('Mandatory reason is required for security audit log.');
      return;
    }

    setIsSubmitting(true);
    setErrorMsg('');
    try {
      if (actionModalType === 'ban') {
        await api('/admin/users/ban', {
          method: 'POST',
          body: JSON.stringify({ accountId: user.accountId, reason: auditReason }),
        });
      } else if (actionModalType === 'unban') {
        await api('/admin/users/unban', {
          method: 'POST',
          body: JSON.stringify({ accountId: user.accountId, reason: auditReason }),
        });
      } else if (actionModalType === 'revoke') {
        await api('/admin/users/revoke-sessions', {
          method: 'POST',
          body: JSON.stringify({ accountId: user.accountId, username: user.cognitoUserId, reason: auditReason }),
        });
      } else if (actionModalType === 'correct') {
        await api('/admin/users/correct-data', {
          method: 'POST',
          body: JSON.stringify({ accountId: user.accountId, displayName: correctNameDraft, reason: auditReason }),
        });
      } else if (actionModalType === 'resend') {
        await api('/admin/users/resend-verification', {
          method: 'POST',
          body: JSON.stringify({ accountId: user.accountId, reason: auditReason }),
        });
      } else if (actionModalType === 'reset') {
        await api('/admin/users/reset-test-account', {
          method: 'POST',
          body: JSON.stringify({ accountId: user.accountId, confirmation: resetConfirmation, reason: auditReason }),
        });
      }
      setActionModalType(null);
      setAuditReason('');
      await onRefresh();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Support action failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyAccountId = async () => {
    try {
      await navigator.clipboard.writeText(user.accountId);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = user.accountId;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    setAccountIdCopied(true);
    window.setTimeout(() => setAccountIdCopied(false), 2000);
  };

  return (
    <section className="panel user-record">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">User record</p>
          <h2>{unmaskedUser ? (displayUser.displayName || displayUser.email) : isCurrentAdmin ? 'Your account' : (displayUser.displayName || displayUser.email)}</h2>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {canReveal && !unmaskedUser ? (
            <button className="primary" type="button" onClick={() => { setRevealModalOpen(true); setAuditReason(''); setErrorMsg(''); }}>
              🔓 Reveal Sensitive Data (Audited)
            </button>
          ) : canReveal && unmaskedUser ? (
            <span style={{ padding: '6px 12px', borderRadius: '8px', background: '#ECFDF5', color: '#059669', fontWeight: 600, fontSize: '13px' }}>
              ✓ Sensitive PII Unmasked
            </span>
          ) : null}
          <button className="secondary" type="button" onClick={onClose}>Close</button>
        </div>
      </div>

      {/* Support Actions Bar */}
      {writable && <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', padding: '12px', backgroundColor: 'var(--surface-hover, rgba(0,0,0,0.02))', borderRadius: '8px', marginBottom: '16px' }}>
        <button
          className={displayUser.accountStatus === 'banned' ? 'secondary' : 'danger'}
          type="button"
          onClick={() => { setActionModalType(displayUser.accountStatus === 'banned' ? 'unban' : 'ban'); setAuditReason(''); setErrorMsg(''); }}
        >
          {displayUser.accountStatus === 'banned' ? 'Unban Account' : 'Ban Account'}
        </button>
        <button
          className="secondary"
          type="button"
          onClick={() => { setActionModalType('revoke'); setAuditReason(''); setErrorMsg(''); }}
        >
          🔐 Revoke All Active Sessions
        </button>
        <button
          className="secondary"
          type="button"
          onClick={() => { setActionModalType('correct'); setCorrectNameDraft(displayUser.displayName || ''); setAuditReason(''); setErrorMsg(''); }}
        >
          ✏️ Edit Display Name
        </button>
        {!displayUser.isEmailVerified && <button className="secondary" type="button" onClick={() => { setActionModalType('resend'); setAuditReason(''); setErrorMsg(''); }}>Resend verification</button>}
        {IS_NON_PRODUCTION_ADMIN && !isCurrentAdmin && <button className="danger" type="button" onClick={() => { setActionModalType('reset'); setResetConfirmation(''); setAuditReason(''); setErrorMsg(''); }}>Reset test account</button>}
      </div>}

      <div className="user-record-grid">
        <div className="user-record-card">
          <h3>Contact and account</h3>
          <dl>
            <div><dt>Email</dt><dd>{displayUser.email}</dd></div>
            <div><dt>Mobile number</dt><dd>{displayUser.mobileNumber || 'Not provided'}</dd></div>
            <div><dt>Address</dt><dd>{displayUser.address || 'Not provided'}</dd></div>
            <div><dt>Email status</dt><dd>{displayUser.isEmailVerified ? 'Verified' : 'Not verified'}</dd></div>
            <div><dt>Phone status</dt><dd>{displayUser.isPhoneVerified ? 'Verified' : 'Not verified'}</dd></div>
            <div><dt>Account ID</dt><dd><code>{displayUser.accountId}</code></dd></div>
            <div><dt>Account type</dt><dd>{humanize(displayUser.accountType)}</dd></div>
            <div><dt>Joined</dt><dd>{date(displayUser.createdAt)}</dd></div>
            <div><dt>Last login</dt><dd>{date(displayUser.lastLoginAt)}</dd></div>
          </dl>
        </div>

        <div className="user-record-card">
          <h3>Subscription & Billing</h3>
          <dl>
            <div><dt>Stored Plan</dt><dd>{humanize(displayUser.subscriptionPlan)}</dd></div>
            <div><dt>Effective Entitlement</dt><dd><strong>{humanize(displayUser.effectiveSubscriptionPlan || displayUser.subscriptionPlan)}</strong></dd></div>
            <div><dt>Billing Provider</dt><dd>{displayUser.billingProvider || 'none'}</dd></div>
            <div><dt>Billing Interval</dt><dd>{displayUser.subscriptionBillingInterval ? `${displayUser.subscriptionBillingInterval}ly` : 'N/A'}</dd></div>
            <div><dt>Subscription Status</dt><dd>{humanize(displayUser.subscriptionStatus || displayUser.accountStatus)}</dd></div>
            <div><dt>Renewal / End Date</dt><dd>{displayUser.renewalDate ? date(displayUser.renewalDate) : 'No auto-renewal'}</dd></div>
          </dl>
        </div>

        <div className="user-record-card"><h3>Usage Quotas & Resets</h3><p className="muted">Usage is enforced and displayed per learner profile below.</p></div>

        <div className="user-record-card">
          <h3>Safety, Privacy & Consent</h3>
          <dl>
            <div><dt>Open Safety Incidents</dt><dd>{displayUser.openSafetyIncidentsCount ?? 0} open</dd></div>
            <div><dt>Deletion Request State</dt><dd>{!displayUser.deletionStatus || displayUser.deletionStatus === 'none' ? 'None' : `${humanize(displayUser.deletionStatus)} (${date(displayUser.deletionRequestedAt)})`}</dd></div>
            <div><dt>Account Export</dt><dd>{humanize(displayUser.exportStatus || 'not_requested')} {displayUser.exportRequestedAt ? `(${date(displayUser.exportRequestedAt)})` : ''}</dd></div>
            <div><dt>Legal Policy Accepted</dt><dd>{displayUser.legalPolicyVersionAccepted || 'Not recorded'}</dd></div>
            <div><dt>Accepted At</dt><dd>{date(displayUser.legalPolicyAcceptedAt)}</dd></div>
            {displayUser.banReason && (
              <div><dt>Ban Reason</dt><dd style={{ color: '#DC2626' }}>{displayUser.banReason} ({displayUser.bannedBy})</dd></div>
            )}
          </dl>
        </div>
      </div>

      {displayUser.learners.map((learner) => (
        <section className="learner-record" key={learner.profileId}>
          <div className="learner-record-heading">
            <div>
              <h3>{learner.displayName}</h3>
              <p>{humanize(learner.proficiencyLevel)} overall · {learner.status || 'Active'}</p>
            </div>
            <code>{learner.profileId}</code>
          </div>

          <div className="learner-personal-grid">
            <span><small>Age</small><strong>{learner.ageGroup ? humanize(learner.ageGroup) : 'Not provided'}</strong></span>
            <span><small>Gender</small><strong>{learner.gender ? humanize(learner.gender) : 'Not provided'}</strong></span>
            <span><small>School / organization</small><strong>{learner.schoolName || 'Not provided'}</strong></span>
            <span><small>Education / employment</small><strong>{learner.occupation ? humanize(learner.occupation) : 'Not provided'}</strong></span>
            <span><small>Native language</small><strong>{learner.nativeLanguage ? humanize(learner.nativeLanguage) : 'Not provided'}</strong></span>
            <span><small>Pronunciation</small><strong>{learner.pronunciationTrack ? humanize(learner.pronunciationTrack) : 'Not provided'}</strong></span>
          </div>

          <h4>English Level Check</h4>
          <div className="domain-record-grid">
            {ENGLISH_LEVEL_DOMAINS.map((domain) => {
              const placement = learner.domainPlacement?.[domain];
              const achievements = [
                placement?.beginnerExitPassed && 'Basic achieved',
                placement?.intermediateExitPassed && 'Intermediate achieved',
                placement?.advancedExitPassed && 'Advanced achieved',
                placement?.masteryPassed && 'Mastery achieved',
              ].filter(Boolean) as string[];
              return (
                <div className="domain-record" key={domain}>
                  <strong>{humanize(domain)}</strong>
                  <span>{humanize(placement?.level || 'not_assessed')}</span>
                  <small>{placement?.lastScore !== undefined ? `Latest score ${placement.lastScore}%` : 'No cloud score'}</small>
                  <div className="achievement-list">
                    {achievements.map((achievement) => <b key={achievement}>{humanize(domain)} · {achievement}</b>)}
                    {!achievements.length && <em>No level achieved</em>}
                  </div>
                </div>
              );
            })}
          </div>

          <h4>Server-enforced usage</h4>
          <div className="domain-record-grid">
            {Object.entries(learner.subscriptionUsage?.features ?? {}).map(([feature, usage]) => <div className="domain-record" key={feature}><strong>{humanize(feature)}</strong><span>{usage.used} / {usage.limit}</span><small>Resets {date(usage.resetsAt)}</small></div>)}
            {!learner.subscriptionUsage && <p className="muted">Usage data unavailable.</p>}
          </div>

          <h4>Server Exam Readiness</h4>
          <div className="domain-record-grid">
            {[['ielts-academic', 'IELTS Academic'], ['ielts-general', 'IELTS General Training'], ['toeic', 'TOEIC'], ['toefl', 'TOEFL iBT']].map(([track, exam]) => {
              const attempt = learner.examReadinessAttempts?.find((candidate) => candidate.track === track);
              return (
                <div className="domain-record" key={exam}>
                  <strong>{exam}</strong>
                  <span>{attempt ? `${attempt.correct ?? 0} / ${attempt.objectiveTotal ?? 0} objective` : 'No attempts'}</span>
                  <small>{attempt ? `${attempt.completed ? 'Completed' : 'In progress'} ${date(attempt.completedAt || attempt.updatedAt)}` : 'No server record'}</small>
                </div>
              );
            })}
          </div>
        </section>
      ))}
      {!displayUser.learners.length && <div className="workspace-empty">This account has no learner profile.</div>}

      {/* Sensitive PII Reveal Modal */}
      {revealModalOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'var(--surface, #ffffff)', padding: '24px', borderRadius: '12px', maxWidth: '450px', width: '90%' }}>
            <h3 style={{ marginTop: 0 }}>🔓 Audit Requirement for Sensitive Data Access</h3>
            <p style={{ fontSize: '13px', opacity: 0.9 }}>
              Revealing unmasked email, phone number, address, school, and gender requires an explicit justification for the immutable audit log.
            </p>
            {errorMsg && <div style={{ color: '#DC2626', fontSize: '13px', marginBottom: '8px' }}>{errorMsg}</div>}
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '12px' }}>
              Justification Reason / Support Ticket #:
              <input
                type="text"
                placeholder="e.g. User requested account verification check #Ticket-9921"
                value={auditReason}
                onChange={(e) => setAuditReason(e.target.value)}
                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--line)', marginTop: '4px' }}
              />
            </label>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="secondary" type="button" onClick={() => setRevealModalOpen(false)}>Cancel</button>
              <button className="primary" type="button" disabled={isSubmitting || !auditReason.trim()} onClick={() => void handleReveal()}>
                {isSubmitting ? 'Verifying...' : 'Confirm & Unmask'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Support Action Confirmation Modal */}
      {actionModalType && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'var(--surface, #ffffff)', padding: '24px', borderRadius: '12px', maxWidth: '450px', width: '90%' }}>
            <h3 style={{ marginTop: 0 }}>
              {actionModalType === 'ban' ? '⛔ Ban Account' : actionModalType === 'unban' ? '✅ Unban Account' : actionModalType === 'revoke' ? '🔐 Revoke Active Sessions' : actionModalType === 'reset' ? '🗑️ Reset Non-Production Test Account' : '✏️ Edit Display Name'}
            </h3>
            {errorMsg && <div style={{ color: '#DC2626', fontSize: '13px', marginBottom: '8px' }}>{errorMsg}</div>}

            {actionModalType === 'reset' && (
              <>
                <p>This permanently queues removal of this development or staging account from Cognito and Shin Q data stores. It cannot be undone.</p>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '12px' }}>
                  Type <code>RESET {user.accountId}</code>
                  <button className="secondary" type="button" onClick={() => void handleCopyAccountId()} style={{ marginLeft: '8px', padding: '4px 8px' }}>
                    {accountIdCopied ? 'Copied' : 'Copy account ID'}
                  </button>
                  <input type="text" value={resetConfirmation} onChange={(event) => setResetConfirmation(event.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--line)', marginTop: '4px' }} />
                </label>
              </>
            )}

            {actionModalType === 'correct' && (
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '12px' }}>
                New Display Name:
                <input
                  type="text"
                  value={correctNameDraft}
                  onChange={(e) => setCorrectNameDraft(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--line)', marginTop: '4px' }}
                />
              </label>
            )}

            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '12px' }}>
              Mandatory Audit Reason:
              <input
                type="text"
                placeholder="e.g. Documented support request / Security measure"
                value={auditReason}
                onChange={(e) => setAuditReason(e.target.value)}
                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--line)', marginTop: '4px' }}
              />
            </label>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="secondary" type="button" onClick={() => setActionModalType(null)}>Cancel</button>
              <button className={actionModalType === 'ban' || actionModalType === 'reset' ? 'danger' : 'primary'} type="button" disabled={isSubmitting || !auditReason.trim() || (actionModalType === 'reset' && resetConfirmation !== `RESET ${user.accountId}`)} onClick={() => void handleExecuteSupportAction()}>
                {isSubmitting ? 'Executing...' : 'Execute Action'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function Metric({ label, value, detail }: { label: string; value: React.ReactNode; detail: string }) {
  return <div className="metric"><span>{label}</span><strong>{value}</strong><small>{detail}</small></div>;
}

function BillingBar({ label, value, total }: { label: string; value: number; total: number }) {
  return <div className="billing-bar"><div><strong>{label}</strong><span>{value}</span></div><div className="billing-track"><i style={{ width: `${Math.round((value / total) * 100)}%` }} /></div></div>;
}

function humanize(value: string): string {
  if (value === 'beginner') return 'Basic';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function date(value?: string): string {
  if (!value) return 'Never';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
}

function money(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase(), maximumFractionDigits: 0 }).format(amount / 100);
}

function aggregateAccountingHistory(
  history: AccountingOverview['businessCosts']['history'],
  range: 'monthly' | 'yearly',
  selectedMonth: string,
): AccountingOverview['businessCosts']['history'] {
  const [selectedYear, selectedMonthNumber] = selectedMonth.split('-').map(Number);
  if (range === 'monthly') {
    const byDate = new Map(history.filter((point) => point.date.startsWith(selectedMonth)).map((point) => [point.date, point]));
    const days = new Date(Date.UTC(selectedYear, selectedMonthNumber, 0)).getUTCDate();
    return Array.from({ length: days }, (_, index) => {
      const date = `${selectedMonth}-${String(index + 1).padStart(2, '0')}`;
      return byDate.get(date) ?? { date, amount: 0, services: [] };
    });
  }
  const months = Array.from({ length: 12 }, (_, index) => `${selectedYear}-${String(index + 1).padStart(2, '0')}`);
  return months.map((month) => {
    const matching = history.filter((point) => point.date.startsWith(month));
    const services = new Map<string, number>();
    matching.flatMap((point) => point.services).forEach((service) => services.set(service.service, (services.get(service.service) ?? 0) + service.amount));
    const groupedServices = [...services].map(([service, amount]) => ({ service, amount }));
    return { date: month, amount: groupedServices.reduce((sum, service) => sum + service.amount, 0), services: groupedServices };
  });
}

function shiftAccountingMonth(month: string, offset: number): string {
  const [year, monthNumber] = month.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, monthNumber - 1 + offset, 1));
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}`;
}

function lastDayOfMonth(month: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  const day = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  return `${month}-${String(day).padStart(2, '0')}`;
}

function pdfSafeText(value: string): string {
  return /[^\u0020-\u007e]/u.test(value) ? 'See receipt for Japanese text' : value;
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url; anchor.download = fileName; anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function accountingMonthLabel(month: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(year, monthNumber - 1, 1)));
}

function accountingServiceColor(service: string): string {
  const palette = ['#0073bb', '#ff9900', '#2ca02c', '#d62728', '#9467bd', '#17becf', '#e377c2', '#8c564b', '#bcbd22', '#1f77b4', '#ff7f0e', '#98df8a', '#ff9896', '#c5b0d5', '#9edae5', '#f7b6d2', '#c49c94', '#dbdb8d', '#393b79', '#637939'];
  const hash = [...service].reduce((value, character) => ((value << 5) - value + character.charCodeAt(0)) | 0, 0);
  return palette[Math.abs(hash) % palette.length];
}

function accountingDateLabel(value: string, range: 'monthly' | 'yearly'): string {
  const [year, month, parsedDay] = value.split('-').map(Number);
  const day = parsedDay || 1;
  return new Intl.DateTimeFormat('en-US', range === 'monthly' ? { month: 'short', day: 'numeric' } : { month: 'short' }).format(new Date(Date.UTC(year, month - 1, day)));
}

function EyeIcon({ hidden }: { hidden: boolean }) {
  return hidden ? (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m3 3 18 18M10.6 10.7a2 2 0 0 0 2.7 2.7M9.9 4.2A10.7 10.7 0 0 1 12 4c5.5 0 9 5.1 9 8a8.7 8.7 0 0 1-1.8 3.4M6.2 6.2C4.2 7.5 3 10 3 12c0 2.9 3.5 8 9 8a10 10 0 0 0 3.5-.6" /></svg>
  ) : (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 12c0-2.9 3.5-8 9-8s9 5.1 9 8-3.5 8-9 8-9-5.1-9-8Z" /><circle cx="12" cy="12" r="3" /></svg>
  );
}

function AdminBrandMark({ compact = false }: { compact?: boolean }) {
  return (
    <span aria-label="Shin Q" className={`admin-brand-mark ${compact ? 'admin-brand-mark-compact' : ''}`.trim()} role="img">
      <img aria-hidden="true" className="admin-brand-logo admin-brand-logo-light" src={`${process.env.PUBLIC_URL}/shinq-wordmark.png`} alt="" />
      <img aria-hidden="true" className="admin-brand-logo admin-brand-logo-dark" src={`${process.env.PUBLIC_URL}/shinq-wordmark-dark.png`} alt="" />
    </span>
  );
}

function ThemeModeButton({
  className = '',
  colorMode,
  onClick,
}: {
  className?: string;
  colorMode: 'light' | 'dark';
  onClick: () => void;
}) {
  const nextMode = colorMode === 'light' ? 'Dark' : 'Light';
  return (
    <button
      aria-label={`Switch to ${nextMode.toLowerCase()} mode`}
      className={`secondary theme-mode-toggle ${className}`.trim()}
      onClick={onClick}
      title={`Switch to ${nextMode.toLowerCase()} mode`}
      type="button"
    >
      {colorMode === 'light' ? <MoonIcon /> : <SunIcon />}
      <span>{nextMode} mode</span>
    </button>
  );
}

function MoonIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.2 15.1A8.5 8.5 0 0 1 8.9 3.8 8.5 8.5 0 1 0 20.2 15.1Z" /></svg>;
}

function SunIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>;
}

function ImageGenerationDialog({
  item,
  hasImage,
  onClose,
  onGenerate,
}: {
  item: VocabularyItem;
  hasImage: boolean;
  onClose: () => void;
  onGenerate: (notes: string, imageModel: ImageModelChoice) => Promise<void>;
}) {
  const [notes, setNotes] = useState('');
  const [imageModel, setImageModel] = useState<ImageModelChoice>('openai-gpt-image-2-medium');
  const [showDetails, setShowDetails] = useState(false);
  const [saving, setSaving] = useState(false);
  const selected = IMAGE_MODELS.find((model) => model.id === imageModel) || IMAGE_MODELS[0];
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (hasImage && !notes.trim()) return;
    setSaving(true);
    await onGenerate(notes.trim(), imageModel).finally(() => setSaving(false));
  };
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <form className="edit-dialog generation-dialog" onSubmit={submit} onMouseDown={(event) => event.stopPropagation()}>
        <div><p className="eyebrow">{hasImage ? 'Replace image' : 'Generate image'}</p><h2>{item.englishWord}</h2></div>
        <label>
          AI image model
          <div className="model-control">
            <select value={imageModel} onChange={(event) => setImageModel(event.target.value as ImageModelChoice)}>
              {IMAGE_MODELS.map((model) => <option key={model.id} value={model.id}>{model.label}</option>)}
            </select>
            <button
              type="button"
              className="model-info-button"
              aria-expanded={showDetails}
              onClick={() => setShowDetails((current) => !current)}
            >
              i <span>Model &amp; price</span>
            </button>
          </div>
        </label>
        {showDetails && (
          <section className="model-details" aria-live="polite">
            <strong>{selected.label}</strong>
            <p>{selected.description}</p>
            <dl>
              <div><dt>Provider</dt><dd>{selected.provider}</dd></div>
              <div><dt>Type</dt><dd>{selected.kind}</dd></div>
              <div><dt>Output</dt><dd>{selected.quality}</dd></div>
              <div><dt>Processing</dt><dd>{selected.region}</dd></div>
              <div><dt>Estimated price</dt><dd>{selected.price}</dd></div>
            </dl>
            <small>Estimate only. A rejected quality check or retry creates another billable generation. Prompt planning and automated review may add a small separate cost.</small>
          </section>
        )}
        <label>
          {hasImage ? 'Why should this image be replaced?' : 'Optional instructions'}
          <textarea
            rows={4}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder={hasImage ? 'Describe what is wrong and what the new image should show or avoid.' : 'Describe any specific scene requirements.'}
            required={hasImage}
          />
        </label>
        <div className="dialog-actions">
          <button type="button" className="secondary" onClick={onClose}>Cancel</button>
          <button className="primary" disabled={saving || (hasImage && !notes.trim())}>
            {saving ? 'Queuing…' : hasImage ? 'Replace image' : 'Generate image'}
          </button>
        </div>
      </form>
    </div>
  );
}

function EditDialog({ item, onClose, onSave }: { item: VocabularyItem; onClose: () => void; onSave: (changes: Record<string, unknown>) => Promise<void> }) {
  const [pronunciation, setPronunciation] = useState(item.pronunciation);
  const [partOfSpeech, setPartOfSpeech] = useState(item.partOfSpeech);
  const [definition, setDefinition] = useState(item.senses?.[0]?.definition || '');
  const [japaneseTranslation, setJapaneseTranslation] = useState(item.senses?.[0]?.japaneseTranslation || '');
  const [japaneseDefinition, setJapaneseDefinition] = useState(item.senses?.[0]?.japaneseDefinition || '');
  const [saving, setSaving] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    await onSave({
      pronunciation,
      partOfSpeech,
      senses: [{ ...item.senses?.[0], definition, japaneseTranslation, japaneseDefinition }],
    }).finally(() => setSaving(false));
  };
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <form className="edit-dialog" onSubmit={submit} onMouseDown={(event) => event.stopPropagation()}>
        <div><p className="eyebrow">Edit vocabulary</p><h2>{item.englishWord}</h2></div>
        <label>IPA pronunciation<input value={pronunciation} onChange={(event) => setPronunciation(event.target.value)} /></label>
        <label>Part of speech<input value={partOfSpeech} onChange={(event) => setPartOfSpeech(event.target.value)} /></label>
        <label>Definition<textarea rows={4} value={definition} onChange={(event) => setDefinition(event.target.value)} /></label>
        <label>Japanese translation<textarea rows={3} value={japaneseTranslation} onChange={(event) => setJapaneseTranslation(event.target.value)} /></label>
        <label>Japanese definition<textarea lang="ja" rows={4} value={japaneseDefinition} onChange={(event) => setJapaneseDefinition(event.target.value)} /></label>
        <div className="dialog-actions"><button type="button" className="secondary" onClick={onClose}>Cancel</button><button className="primary" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button></div>
      </form>
    </div>
  );
}

function DictionaryVerification({ sense }: { sense?: VocabularySense }) {
  const references = sense?.dictionaryReferences || [];
  const labels: Record<string, string> = {
    oxford: 'Oxford',
    merriam_webster: 'Merriam-Webster',
    japanese_licensed_dictionary: 'Licensed Japanese dictionary',
  };
  return (
    <div className={`dictionary-verification ${references.length ? 'verified' : 'pending'}`}>
      <strong>{references.length ? 'Dictionary verification' : 'Dictionary verification pending'}</strong>
      {references.length ? (
        <div className="source-chips">
          {references.map((reference, index) => (
            reference.sourceUrl
              ? <a key={`${reference.provider}-${index}`} href={reference.sourceUrl} target="_blank" rel="noreferrer">{labels[reference.provider]}</a>
              : <span key={`${reference.provider}-${index}`}>{labels[reference.provider]}</span>
          ))}
        </div>
      ) : (
        <span>Not yet checked against licensed Oxford, Merriam-Webster, and Japanese dictionary data.</span>
      )}
    </div>
  );
}

function PostalCodeTesterView() {
  const [testZip, setTestZip] = useState('104-0061');
  const [isJapaneseMode, setIsJapaneseMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [pageDraft, setPageDraft] = useState('1');
  const [testBuildingInput, setTestBuildingInput] =
    useState('丸の内パークビルディング 3階 301号室');
  const [directoryEntries, setDirectoryEntries] = useState<PostalDirectoryEntry[]>([]);
  const [directoryLoading, setDirectoryLoading] = useState(true);
  const [directoryError, setDirectoryError] = useState('');

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
    setPageDraft('1');
  };

  useEffect(() => {
    let active = true;
    setDirectoryLoading(true);
    setDirectoryError('');
    loadPostalDirectory()
      .then((entries) => {
        if (active) setDirectoryEntries(entries);
      })
      .catch((error) => {
        if (active) {
          setDirectoryError(
            error instanceof Error ? error.message : 'Unable to load the postal directory.'
          );
        }
      })
      .finally(() => {
        if (active) setDirectoryLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const entriesByZip = useMemo(
    () => new Map(directoryEntries.map((entry) => [entry.zip, entry])),
    [directoryEntries]
  );
  const addressForZip = useCallback(
    (rawZip: string, japanese: boolean): PostalCodeAddress | null => {
      const zip = rawZip.replace(/[^\d]/gu, '');
      if (zip.length !== 7) return null;
      const entry = entriesByZip.get(zip);
      if (!entry) return null;
      return {
        postalCode: entry.code,
        prefecture: japanese ? entry.prefectureJp : entry.prefectureEn,
        city: japanese ? entry.cityJp : entry.cityEn,
        street: japanese ? entry.streetJp : entry.streetEn,
        country: japanese ? '日本' : 'Japan',
        prefectureJp: entry.prefectureJp,
        cityJp: entry.cityJp,
        streetJp: entry.streetJp,
        prefectureEn: entry.prefectureEn,
        cityEn: entry.cityEn,
        streetEn: entry.streetEn,
      };
    },
    [entriesByZip]
  );
  const addressResult = useMemo(
    () => addressForZip(testZip, isJapaneseMode),
    [addressForZip, isJapaneseMode, testZip]
  );

  const quickSamples = [
    { label: '104-0061 (Tokyo Chuo-ku Ginza)', zip: '104-0061' },
    { label: '251-0028 (Kanagawa Fujisawa)', zip: '251-0028' },
    { label: '150-0001 (Tokyo Shibuya Jingumae)', zip: '150-0001' },
    { label: '160-0022 (Tokyo Shinjuku)', zip: '160-0022' },
    { label: '530-0001 (Osaka Umeda)', zip: '530-0001' },
    { label: '600-8001 (Kyoto Shimogyo)', zip: '600-8001' },
    { label: '810-0001 (Fukuoka Tenjin)', zip: '810-0001' },
    { label: '060-0001 (Sapporo Odori)', zip: '060-0001' },
    { label: '460-0008 (Nagoya Sakae)', zip: '460-0008' },
  ];

  const filteredEntries = useMemo(
    () => searchPostalDirectory(directoryEntries, deferredSearchTerm),
    [deferredSearchTerm, directoryEntries]
  );

  const totalPages = Math.max(1, Math.ceil(filteredEntries.length / pageSize));
  const paginatedEntries = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredEntries.slice(start, start + pageSize);
  }, [filteredEntries, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  useEffect(() => {
    setPageDraft(String(currentPage));
  }, [currentPage]);

  const goToDraftPage = () => {
    const requestedPage = Number(pageDraft);
    if (!Number.isInteger(requestedPage)) {
      setPageDraft(String(currentPage));
      return;
    }
    setCurrentPage(Math.min(totalPages, Math.max(1, requestedPage)));
  };
  const searchPending = searchTerm !== deferredSearchTerm;

  const [diagnosticLog, setDiagnosticLog] = useState<string[]>([]);

  const runFullDiagnostics = () => {
    const logs: string[] = [];
    logs.push('Running Japan-wide postal directory checks...');
    const testCases = [
      { zip: '1040061', expectedPref: 'Tokyo', expectedCity: 'Chuo-ku', expectedStreet: 'Ginza' },
      {
        zip: '2510028',
        expectedPref: 'Kanagawa',
        expectedCity: 'Fujisawa City',
        expectedStreet: 'Honkugenuma',
      },
      {
        zip: '1500001',
        expectedPref: 'Tokyo',
        expectedCity: 'Shibuya-ku',
        expectedStreet: 'Jingumae',
      },
      {
        zip: '5300001',
        expectedPref: 'Osaka',
        expectedCity: 'Kita-ku, Osaka',
        expectedStreet: 'Umeda',
      },
      {
        zip: '6008001',
        expectedPref: 'Kyoto',
        expectedCity: 'Shimogyo-ku, Kyoto',
        expectedStreet: 'Shincho',
      },
      {
        zip: '8100001',
        expectedPref: 'Fukuoka',
        expectedCity: 'Chuo-ku, Fukuoka',
        expectedStreet: 'Tenjin',
      },
      {
        zip: '0600001',
        expectedPref: 'Hokkaido',
        expectedCity: 'Chuo-ku, Sapporo',
        expectedStreet: 'Kita 1-jo Nishi',
      },
      {
        zip: '4600008',
        expectedPref: 'Aichi',
        expectedCity: 'Naka-ku, Nagoya',
        expectedStreet: 'Sakae',
      },
      {
        zip: '1006001',
        expectedPref: 'Tokyo',
        expectedCity: 'Chiyoda-ku',
        expectedStreet: 'Kasumigaseki Kasumigaseki Bldg (1st floor)',
      },
    ];

    let passedCount = 0;
    for (const tc of testCases) {
      const res = addressForZip(tc.zip, false);
      if (!res) {
        logs.push(`FAIL: Postal code ${tc.zip} returned no directory entry.`);
        continue;
      }
      const matches =
        normalizePostalSearchValue(res.prefectureEn) ===
          normalizePostalSearchValue(tc.expectedPref) &&
        normalizePostalSearchValue(res.cityEn) === normalizePostalSearchValue(tc.expectedCity) &&
        normalizePostalSearchValue(res.streetEn) === normalizePostalSearchValue(tc.expectedStreet);
      if (matches) {
        passedCount += 1;
        logs.push(`PASS: ${res.postalCode} -> ${res.streetEn}, ${res.cityEn}, ${res.prefectureEn}`);
      } else {
        logs.push(
          `FAIL: ${res.postalCode} returned ${res.streetEn}, ${res.cityEn}, ${res.prefectureEn}.`
        );
      }
    }

    logs.push(`Completed: ${passedCount}/${testCases.length} directory checks passed.`);
    setDiagnosticLog(logs);
  };

  return (
    <div className="workspace">
      <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Japanese Address Verification</p>
            <h2>Postal Code</h2>
          </div>
          <p className="muted">
            Official Japan Post area and business-office addresses served through the admin
            CloudFront distribution. The lookup and registry use the same complete dataset.
          </p>
        </div>

        <div className="admin-create-form" style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={{ flex: 1, minWidth: 240 }}>
              Postal Code (e.g. 104-0061)
              <input
                type="text"
                placeholder="e.g. 104-0061"
                value={testZip}
                onChange={(e) => setTestZip(e.target.value)}
              />
            </label>

            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <button
                type="button"
                className={`secondary ${isJapaneseMode ? 'active' : ''}`}
                onClick={() => setIsJapaneseMode(!isJapaneseMode)}
              >
                {isJapaneseMode ? '🇯🇵 Japanese (Kanji)' : '🌐 English (Romaji)'}
              </button>

              <button
                type="button"
                onClick={runFullDiagnostics}
                disabled={directoryLoading || Boolean(directoryError)}
              >
                Run directory checks
              </button>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <p className="eyebrow" style={{ marginBottom: 8 }}>
              Quick Test Samples:
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {quickSamples.map((sample) => (
                <button
                  key={sample.zip}
                  type="button"
                  className={`sample-chip ${testZip === sample.zip ? 'active' : ''}`}
                  onClick={() => setTestZip(sample.zip)}
                >
                  {sample.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {directoryLoading ? (
          <div className="workspace-empty" style={{ marginTop: 24 }}>
            <ShinQLoader message="Loading the complete postal directory..." />
          </div>
        ) : directoryError ? (
          <div className="alert error" style={{ marginTop: 24 }} role="alert">
            {directoryError}
          </div>
        ) : addressResult ? (
          <div className="postal-result-card">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}
            >
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>
                📍{' '}
                {isJapaneseMode ? '住所検証結果 (Address Output)' : 'Address Verification Output'}
              </h3>
              <span
                style={{
                  padding: '3px 10px',
                  borderRadius: 12,
                  background: 'var(--mint)',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              >
                Admin CDN registry
              </span>
            </div>

            <div className="postal-grid">
              <div className="postal-field">
                <p className="label">Postal Code</p>
                <p className="val">{addressResult.postalCode}</p>
              </div>
              <div className="postal-field">
                <p className="label">Country</p>
                <p className="val">{addressResult.country}</p>
              </div>
              <div className="postal-field">
                <p className="label">Prefecture</p>
                <p className="val">{addressResult.prefecture}</p>
              </div>
              <div className="postal-field">
                <p className="label">City / Ward</p>
                <p className="val">{addressResult.city || '(Manual Entry Required)'}</p>
              </div>
              <div className="postal-field">
                <p className="label">Street District</p>
                <p className="val">{addressResult.street || '(Manual Entry Required)'}</p>
              </div>
            </div>

            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
              <p style={{ margin: 0, fontSize: '0.9rem' }}>
                <strong>Full Formatted Address:</strong>{' '}
                {[
                  addressResult.street,
                  addressResult.city,
                  addressResult.prefecture,
                  addressResult.country,
                  addressResult.postalCode,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </p>
            </div>
          </div>
        ) : (
          <div className="alert error" style={{ marginTop: 24 }}>
            Postal code <strong>{testZip}</strong> is invalid or not present. Enter a 7-digit
            Japanese postal code, such as 104-0061.
          </div>
        )}

        {diagnosticLog.length > 0 && (
          <div className="diagnostic-terminal">
            {diagnosticLog.map((line, idx) => (
              <div key={idx} style={{ marginBottom: 4 }}>
                {line}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="panel" style={{ marginTop: 24 }}>
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Japanese Address & Building Transliteration Engine</p>
            <h2>Building, Floor & Room Translator</h2>
          </div>
          <p className="muted">
            Auto-translates Japanese floor counts (`１階` ➔ `1st floor`, `地下1階` ➔ `Basement 1st
            floor`), room numbers (`101号室` ➔ `Room 101`), and building terms (`タワー` ➔ `Tower`,
            `パークビルディング` ➔ `Park Building`, `ビル` ➔ `Bldg`).
          </p>
        </div>

        <div className="admin-create-form" style={{ marginTop: 16 }}>
          <label style={{ width: '100%' }}>
            Japanese Building / Floor / Room Input (e.g. 丸の内パークビルディング 3階 301号室)
            <input
              type="text"
              placeholder="e.g. 丸の内パークビルディング 3階 301号室"
              value={testBuildingInput}
              onChange={(e) => setTestBuildingInput(e.target.value)}
            />
          </label>

          <div style={{ marginTop: 12 }}>
            <p className="eyebrow" style={{ marginBottom: 8 }}>
              Quick Building & Floor Test Presets:
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[
                '１階',
                '２２階 202号室',
                '地下1階',
                '丸の内パークビルディング 3階',
                '六本木ヒルズタワー 10階 1001号室',
                '銀座グランドビル B1階',
              ].map((sample) => (
                <button
                  key={sample}
                  type="button"
                  className={`sample-chip ${testBuildingInput === sample ? 'active' : ''}`}
                  onClick={() => setTestBuildingInput(sample)}
                >
                  {sample}
                </button>
              ))}
            </div>
          </div>
        </div>

        {testBuildingInput.trim() && (
          <div className="postal-result-card">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
              }}
            >
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>
                🏢 English Building & Floor Translation Output
              </h3>
              <span
                style={{
                  padding: '3px 10px',
                  borderRadius: 12,
                  background: 'var(--mint)',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              >
                Hepburn & Ordinal Engine
              </span>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: 16,
                marginTop: 12,
              }}
            >
              <div>
                <p
                  className="label"
                  style={{ margin: 0, fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 600 }}
                >
                  Original Japanese Input
                </p>
                <p
                  className="val"
                  style={{ margin: '4px 0 0 0', fontWeight: 700, fontSize: '1.1rem' }}
                >
                  {testBuildingInput}
                </p>
              </div>
              <div>
                <p
                  className="label"
                  style={{ margin: 0, fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 600 }}
                >
                  Auto-Translated English Output
                </p>
                <p
                  className="val"
                  style={{
                    margin: '4px 0 0 0',
                    fontWeight: 700,
                    fontSize: '1.2rem',
                    color: 'var(--mint-ink)',
                  }}
                >
                  {translateJapaneseFloorAndRoom(testBuildingInput)}
                </p>
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="panel" style={{ marginTop: 24 }}>
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Complete Japan Post Directory</p>
            <h2>
              {directoryLoading
                ? 'Loading postal registry'
                : `${filteredEntries.length.toLocaleString()} result${
                    filteredEntries.length === 1 ? '' : 's'
                  }`}
            </h2>
            {!directoryLoading && !directoryError && (
              <p className="muted" style={{ margin: '4px 0 0' }}>
                {directoryEntries.length.toLocaleString()} total postal codes. Leave search blank to
                browse everything.
              </p>
            )}
          </div>
          <div style={{ width: 320, maxWidth: '100%' }}>
            <input
              type="search"
              aria-label="Search the complete postal directory"
              placeholder="Tokyo, Honkugenuma, 251-0028..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              disabled={directoryLoading || Boolean(directoryError)}
            />
            {searchPending && (
              <span className="visually-hidden" role="status">
                Searching directory
              </span>
            )}
          </div>
        </div>

        <div style={{ overflowX: 'auto', marginTop: 16 }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Postal Code</th>
                <th>Japanese (Kanji)</th>
                <th>English Romaji</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {directoryLoading && (
                <tr>
                  <td colSpan={4}>Loading the complete postal directory...</td>
                </tr>
              )}
              {!directoryLoading && directoryError && (
                <tr>
                  <td colSpan={4}>{directoryError}</td>
                </tr>
              )}
              {!directoryLoading && !directoryError && !paginatedEntries.length && (
                <tr>
                  <td colSpan={4}>No postal addresses match “{deferredSearchTerm.trim()}”.</td>
                </tr>
              )}
              {!directoryLoading &&
                !directoryError &&
                paginatedEntries.map((item) => (
                  <tr key={item.code}>
                    <td style={{ fontWeight: 700, fontFamily: 'monospace' }}>
                      <button
                        type="button"
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--mint-ink)',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          padding: 0,
                          fontSize: '0.95rem',
                        }}
                        onClick={() => setTestZip(item.code)}
                      >
                        {item.code}
                      </button>
                    </td>
                    <td>
                      {item.prefectureJp} {item.cityJp} {item.streetJp}
                    </td>
                    <td style={{ fontWeight: 600 }}>
                      {item.streetEn ? `${item.streetEn}, ` : ''}
                      {item.cityEn}, {item.prefectureEn}
                    </td>
                    <td>
                      <span
                        style={{
                          padding: '3px 8px',
                          borderRadius: 10,
                          background: 'var(--mint-soft)',
                          color: 'var(--mint-ink)',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                        }}
                      >
                        Directory
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            marginTop: 16,
            paddingTop: 12,
            borderTop: '1px solid var(--line)',
          }}
        >
          <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--muted)' }}>
            {filteredEntries.length
              ? `Showing ${((currentPage - 1) * pageSize + 1).toLocaleString()}–${Math.min(
                  currentPage * pageSize,
                  filteredEntries.length
                ).toLocaleString()} of ${filteredEntries.length.toLocaleString()}`
              : 'Showing 0 results'}
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.85rem' }}>
              Rows
              <select
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setCurrentPage(1);
                  setPageDraft('1');
                }}
                disabled={directoryLoading || Boolean(directoryError)}
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
              </select>
            </label>
            <button
              className="secondary"
              disabled={directoryLoading || currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              style={{ padding: '6px 12px' }}
            >
              Previous
            </button>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, padding: '0 4px' }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="secondary"
              disabled={directoryLoading || currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              style={{ padding: '6px 12px' }}
            >
              Next
            </button>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.85rem' }}>
              Go to
              <input
                type="number"
                min={1}
                max={totalPages}
                inputMode="numeric"
                value={pageDraft}
                onChange={(event) => setPageDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') goToDraftPage();
                }}
                disabled={directoryLoading || Boolean(directoryError)}
                style={{ width: 76, padding: '6px 8px' }}
                aria-label="Page number"
              />
            </label>
            <button
              className="secondary"
              onClick={goToDraftPage}
              disabled={directoryLoading || Boolean(directoryError)}
              style={{ padding: '6px 12px' }}
            >
              Go
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default App;
